import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
} from '@tanstack/react-table';
import { GenericCell } from './cells/GenericCell';
import { MergedTomaCatalogCell, VALID_CATALOG_FIELDS, type CatalogField } from './cells/MergedTomaCatalogCell';
import { EditableCatalogCell } from './cells/EditableCatalogCell';
import type { ColumnFormat } from '../utils/columnFormatters';
import { getStylePresetClassName } from '../utils/columnStylePresets';
import { slugifyTagKey } from '../utils/slugifyTagKey';
import { debugStep } from '../utils/debugStep';
import {
  ChevronUp,
  ChevronDown,
  Settings,
  RefreshCw,
  ExternalLink,
  GripVertical,
  GitCompare,
  X,
  List,
  Grid3X3,
  Bell,
  CheckSquare,
  Square,
  Flag,
  Search
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import ImageModal from './ImageModal';
import ExportModal from './ExportModal';
import ProgressToast from './ProgressToast';
import { exportToExcel, generateDefaultFilename } from '../utils/exportToExcel';
import { formatFecha, getSemanaIso } from '../utils/dateFormat';
import BuyBoxIndicator from './BuyBoxIndicator';
import BuyBoxVariants from './BuyBoxVariants';
import { BulkAlertModal } from './alerts';
import TableNavigation from './TableNavigation';
import DataIssuesModal from './DataIssuesModal';
import {
  parseRawTags,
  parseNumericValue,
  getSalePriceValue,
  getMultiplierValue,
  formatCurrencyValue,
  renderCalculatedPriceBadge,
  formatMultiplierLabel,
  UNIT_PRICE_CLIENT_ID,
  TIA_ECUADOR_CLIENT_ID,
  MPU_KEYS,
  MPUM_KEYS
} from '../utils/tableHelpers';
import { createComparisonColumns as buildComparisonColumnsExternal } from '../hooks/useComparisonColumns';
import { useAuth } from '../contexts/AuthContext';
import { useExtendedMode } from '../contexts/ExtendedModeContext';
import ProductDetailModal from './debug/ProductDetailModal';
import BulkActionBar from './publicaciones/BulkActionBar';
import type { TaxonomyTree } from './publicaciones/BulkEditModal';
// Taxonomía Samsung v6.7 — usada por BulkEditModal cuando el cliente activo
// es Samsung. Para otros clientes pasamos `null` y el modal cae en input texto
// libre (TODO: agregar taxonomies para LG, TIA, etc. cuando estén definidas).
import samsungTaxonomyJson from '../taxonomies/samsung-v6.7.json';
import {
  saveColumnPreferences as saveColumnPreferencesFirebase,
  loadColumnPreferences as loadColumnPreferencesFirebase,
  migrateLocalStorageToFirestore
} from '../services/userPreferences';
import { resolveTemplateOrder } from '../services/columnTemplates';

interface TableData {
  [key: string]: any;
}

interface TagColumnMeta {
  field: string;
  tagKey: string;
  tagDisplay: string;
}

interface DataTableProps {
  tableName: string;
  hideFilters?: boolean;
  filters?: any;
  displayName?: string; // Nuevo parámetro para pasar el nombre correcto de la toma
  comparisonMode?: boolean; // Modo comparación
  comparisonData?: any[]; // Datos de productos en comparación
  onRemoveFromComparison?: (productId: string) => void; // Función para remover de comparación
  onAddToComparison?: (product: any) => void; // Función para agregar a comparación
  comparisonProducts?: any[]; // Lista de productos en comparación para detectar estado
  viewMode?: 'table' | 'grid';
  onViewModeChange?: (newViewMode: 'table' | 'grid') => void;
  onClearAll?: () => void;
  currentTableProducts?: any[]; // Productos disponibles en la toma actual
  currentTableInfo?: { name: string; date?: string }; // Información de la toma actual
  isTableTransitioning?: boolean; // Nueva prop para controlar la transición
  enableTags?: boolean; // Habilitar/deshabilitar columnas de tags
  enableSku?: boolean; // Habilitar/deshabilitar columna SKU editable
  clientId?: string;
  // Modo preview embebido (admin Column Config). Cuando está activo:
  // - usa data sample local (no fetch) y un columnConfigOverride en lugar del schema endpoint
  // - oculta filtros, acciones, paginación, comparación, alertas, exports
  previewMode?: boolean;
  previewSampleRows?: TableData[];
  columnConfigOverride?: any;
  // Orden de columnas dictado por un template (PT-#67). Solo se aplica en
  // previewMode: fuerza columnOrder + visibility a los ids ordenados del
  // template (system cols primero). En producción NO se usa este prop —
  // el reorder de export vive en handleExport / ExportPanel.
  templateColumnOrder?: string[];
}

interface TableResponse {
  data: TableData[];
  total: number;
  totalUnfiltered: number;
  limit: number;
  offset: number;
  pages?: number;
  totalPages?: number;
  tagColumns?: TagColumnMeta[];
}

const DataTable: React.FC<DataTableProps> = ({
  tableName,
  hideFilters = false,
  filters: externalFilters,
  displayName,
  comparisonMode = false,
  comparisonData = [],
  onRemoveFromComparison,
  onAddToComparison,
  comparisonProducts = [],
  viewMode,
  onViewModeChange,
  onClearAll,
  currentTableProducts = [],
  currentTableInfo,
  isTableTransitioning = false,
  enableTags = true,
  enableSku = false,
  clientId,
  previewMode = false,
  previewSampleRows,
  columnConfigOverride,
  templateColumnOrder,
}) => {
  const { t, i18n } = useTranslation();
  const { currentUser, userData } = useAuth();
  const isSuperadmin = userData?.user_type === 'superadmin';
  const canEditCatalog = userData?.user_type === 'admin' || userData?.user_type === 'superadmin';
  const { extendedMode } = useExtendedMode();
  // ID seleccionado para abrir el ProductDetailModal (LUPA). Solo aplica con extendedMode ON.
  const [selectedDetailId, setSelectedDetailId] = useState<string | null>(null);

  // Debug: Log props deshabilitado para evitar spam

  const [data, setData] = useState<TableData[]>([]);
  const [columns, setColumns] = useState<ColumnDef<TableData>[]>([]);
  const [loading, setLoading] = useState(true);
  const hadDataRef = useRef(false);
  const [tagColumnsMeta, setTagColumnsMeta] = useState<TagColumnMeta[]>([]);
  // Snapshot del columnConfig vigente — usado por el dropdown del engranaje
  // para mostrar displayName en lugar del id raw.
  const [columnConfigSnapshot, setColumnConfigSnapshot] = useState<any>(null);
  const isUnitPriceClient = clientId === UNIT_PRICE_CLIENT_ID;
  const isTiaClient = clientId === TIA_ECUADOR_CLIENT_ID;

  // Estado para manejar filas expandidas y sus variantes
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const expandedRowsRef = useRef<Set<string>>(new Set());
  const [buyboxVariants, setBuyboxVariants] = useState<{ [key: string]: any[] }>({});
  const [loadingVariants, setLoadingVariants] = useState<Set<string>>(new Set());

  // Estados para SKU
  const [skus, _setSkus] = useState<{ [id_catalogo: string]: string }>({});
  const [_editingSku, _setEditingSku] = useState<string | null>(null);
  const [skuValues, _setSkuValues] = useState<{ [id_catalogo: string]: string }>({});
  const skusRef = useRef<{ [id_catalogo: string]: string }>({});
  const skuValuesRef = useRef<{ [id_catalogo: string]: string }>({});
  const editingSkuRef = useRef<string | null>(null);
  const handleSkuEditRef = useRef<(id_catalogo: string) => void>(() => {});
  const handleSkuSaveRef = useRef<(id_catalogo: string) => void>(() => {});
  const handleSkuCancelRef = useRef<(id_catalogo: string) => void>(() => {});
  const handleSkuChangeRef = useRef<(id_catalogo: string, value: string) => void>(() => {});

  type SkusMap = { [id_catalogo: string]: string };
  type SetStateUpdater<T> = T | ((prev: T) => T);

  const setSkus = useCallback((valueOrUpdater: SetStateUpdater<SkusMap>) => {
    _setSkus(prev => {
      const next = typeof valueOrUpdater === 'function'
        ? (valueOrUpdater as (prevState: SkusMap) => SkusMap)(prev)
        : valueOrUpdater;
      skusRef.current = next;
      return next;
    });
  }, []);

  const setSkuValues = useCallback((valueOrUpdater: SetStateUpdater<SkusMap>) => {
    _setSkuValues(prev => {
      const next = typeof valueOrUpdater === 'function'
        ? (valueOrUpdater as (prevState: SkusMap) => SkusMap)(prev)
        : valueOrUpdater;
      skuValuesRef.current = next;
      return next;
    });
  }, []);

  const setEditingSku = useCallback((value: string | null) => {
    editingSkuRef.current = value;
    _setEditingSku(value);
  }, []);

  // Estados para selección de productos y alertas masivas
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [showBulkAlertModal, setShowBulkAlertModal] = useState(false);

  // Data Issues modal (Phase 1 — Reportar/Editar issues por row)
  const [dataIssuesModalRow, setDataIssuesModalRow] = useState<any | null>(null);

  // Función para toggle de selección de producto.
  // Acepta opcionalmente un rowIndex (lógico, sobre table.getRowModel().rows) para
  // alimentar el rastro "última row tocada" — usado como foco inicial del modo
  // navegación con teclado.
  const toggleProductSelection = useCallback((productId: string, rowIndex?: number) => {
    if (typeof rowIndex === 'number') {
      lastSelectedRowIndexRef.current = rowIndex;
    }
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  }, []);

  // Función para seleccionar/deseleccionar todos los productos de la página actual
  const toggleSelectAll = useCallback(() => {
    const currentPageIds = data.map(row => row.id_catalogo || row.ID_CATALOGO || row.id);
    const allSelected = currentPageIds.every(id => selectedProducts.has(id));

    if (allSelected) {
      // Deseleccionar todos
      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        currentPageIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Seleccionar todos
      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        currentPageIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  }, [data, selectedProducts]);

  // Obtener productos seleccionados para el modal
  const getSelectedProductsForAlert = useCallback(() => {
    return data
      .filter(row => {
        const productId = row.id_catalogo || row.ID_CATALOGO || row.id;
        return selectedProducts.has(productId);
      })
      .map(row => ({
        id_catalogo: row.id_catalogo || row.ID_CATALOGO || row.id,
        title: row.title || row.titulo || row.TITLE || 'Sin título',
        sitio: row.sitio || row.SITIO || 'N/A',
        sale_price: row.sale_price || row.SALE_PRICE || row.precio,
        imagen: row.imagen || row.IMAGEN || row.image
      }));
  }, [data, selectedProducts]);

  // Limpiar selección cuando cambia la tabla
  useEffect(() => {
    setSelectedProducts(new Set());
  }, [tableName]);

  // ---- BulkActionBar (Publicaciones extended mode) ------------------------
  // selectedIds: array para pasar al componente (id_catalogo).
  const selectedIds = useMemo<string[]>(() => Array.from(selectedProducts), [selectedProducts]);

  const clearSelection = useCallback(() => {
    setSelectedProducts(new Set());
  }, []);

  // ---- Keyboard navigation (BulkActionBar) --------------------------------
  // focusedRowIndex es un índice LÓGICO sobre table.getRowModel().rows (no DOM).
  // Solo aplica cuando la BulkActionBar está visible (extendedMode + selección).
  // null = sin foco / modo inactivo.
  const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);
  // Rastreamos la última row seleccionada por el user para que sea el foco inicial.
  const lastSelectedRowIndexRef = useRef<number | null>(null);
  // Refs a los <tr> para scrollIntoView cuando cambia el foco.
  const rowRefsMap = useRef<Map<number, HTMLTableRowElement | null>>(new Map());
  // Ref al table de TanStack — el listener de keydown necesita la lista de rows
  // actual (paginada/ordenada/filtrada) sin recapturar closures stale.
  const tableRef = useRef<any>(null);

  // Trigger un refetch sutil al terminar una acción bulk. Reusamos el SAME
  // `tableName` para forzar el efecto que recarga data (key change vía
  // bumpRefresh). El refetch real ya lo gestiona useEffect interno; acá sólo
  // bumpeamos un nonce que algunos hooks observan.
  const [bulkRefreshNonce, setBulkRefreshNonce] = useState(0);
  const handleAfterBulkAction = useCallback(() => {
    setBulkRefreshNonce((n) => n + 1);
  }, []);

  // Cliente Samsung — pasamos la taxonomía v6.7 al BulkEditModal. Para otros
  // clientes pasamos null (el modal cae en inputs de texto libre).
  const SAMSUNG_CLIENT_ID = 'O6BlxUiuliGjmWUfwNj1';
  const bulkTaxonomy = useMemo<TaxonomyTree | null>(() => {
    if (clientId === SAMSUNG_CLIENT_ID) {
      return samsungTaxonomyJson as unknown as TaxonomyTree;
    }
    return null;
  }, [clientId]);

  // Función para verificar si un producto está en comparación
  // Función para manejar el toggle de expansión de buybox
  // Sincronizar ref con estado
  useEffect(() => {
    expandedRowsRef.current = expandedRows;
  }, [expandedRows]);

  const handleBuyboxToggle = useCallback((productId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      expandedRowsRef.current = newSet;
      return newSet;
    });
  }, []);

  // Función para verificar si una fila está expandida
  const isRowExpanded = useCallback((productId: string) => {
    return expandedRowsRef.current.has(productId);
  }, []);

  // Función para manejar cuando se cargan las variantes
  const handleVariantsLoaded = useCallback((productId: string, variants: any[]) => {
    setBuyboxVariants(prev => {
      const updated = {
        ...prev,
        [productId]: variants
      };
      return updated;
    });
    setLoadingVariants(prev => {
      const newSet = new Set(prev);
      newSet.delete(productId);
      return newSet;
    });
  }, []);

  // Funciones para manejar SKUs
  const loadSkus = useCallback(async (catalogIds: string[]) => {
    if (!enableSku || catalogIds.length === 0) return;

    try {
      const response = await api.get('/api/skus', {
        params: { id_catalogos: catalogIds.join(',') }
      });
      if (response.data.skus) {
        setSkus(response.data.skus);
        setSkuValues(response.data.skus);
      }
    } catch (error) {
      console.error('Error loading SKUs:', error);
    }
  }, [enableSku]);

  const saveSku = useCallback(async (id_catalogo: string, sku: string) => {
    try {
      await api.post('/api/skus', { id_catalogo, sku });
      setSkus(prev => ({ ...prev, [id_catalogo]: sku }));
      setSkuValues(prev => ({ ...prev, [id_catalogo]: sku }));
    } catch (error) {
      console.error('Error saving SKU:', error);
    }
  }, []);

  const handleSkuEdit = useCallback((id_catalogo: string) => {
    // Inicializar el valor en skuValues si no existe
    setSkuValues(prev => {
      if (!(id_catalogo in prev)) {
        return { ...prev, [id_catalogo]: skus[id_catalogo] || '' };
      }
      return prev;
    });
    setEditingSku(id_catalogo);
  }, [skus]);

  const handleSkuSave = useCallback(async (id_catalogo: string) => {
    const newValue = skuValues[id_catalogo] || '';
    await saveSku(id_catalogo, newValue);
    setEditingSku(null);
  }, [skuValues, saveSku]);

  const handleSkuCancel = useCallback((id_catalogo: string) => {
    setSkuValues(prev => ({ ...prev, [id_catalogo]: skus[id_catalogo] || '' }));
    setEditingSku(null);
  }, [skus]);

  const handleSkuChange = useCallback((id_catalogo: string, value: string) => {
    setSkuValues(prev => {
      const newValues = { ...prev, [id_catalogo]: value };
      return newValues;
    });
  }, []);

  useEffect(() => {
    handleSkuEditRef.current = handleSkuEdit;
  }, [handleSkuEdit]);

  useEffect(() => {
    handleSkuSaveRef.current = handleSkuSave;
  }, [handleSkuSave]);

  useEffect(() => {
    handleSkuCancelRef.current = handleSkuCancel;
  }, [handleSkuCancel]);

  useEffect(() => {
    handleSkuChangeRef.current = handleSkuChange;
  }, [handleSkuChange]);

  const isProductInComparison = (product: any): boolean => {
    // En modo comparación, todos los productos mostrados están en comparación
    if (comparisonMode) {
      return true;
    }
    
    // Si no hay productos en comparación, retornar false inmediatamente
    if (!comparisonProducts || comparisonProducts.length === 0) {
      return false;
    }
    
    // Usar id_catalogo como ID único
    const productId = product.id_catalogo || product.ID_CATALOGO || product.id;
    
    if (!productId) {
      return false; // Si no tiene ID, no puede estar en comparación
    }
    
    // Buscar en la lista de productos usando múltiples formas de identificación (igual que en addToComparison)
    const isInComparison = comparisonProducts.some(comparisonProduct => {
      const existingId = comparisonProduct.comparisonId || comparisonProduct.id_catalogo || comparisonProduct.ID_CATALOGO || comparisonProduct.id;
      return existingId === productId;
    });
    
    return isInComparison;
  };
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // EXTENDED MODE — toggle visibilidad de la columna LUPA según el flag global.
  // La columna existe siempre en el array (para no reconstruir columnas en cada toggle),
  // pero se oculta cuando extendedMode = false.
  useEffect(() => {
    setColumnVisibility(prev => {
      const desired = !!extendedMode;
      if (prev.extended_detail === desired) return prev;
      return { ...prev, extended_detail: desired };
    });
  }, [extendedMode]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [precioReferencia, setPrecioReferencia] = useState('');
  const precioReferenciaRef = useRef(precioReferencia);

  // Brand enrichment frontend deshabilitado a propósito (2026-05-07).
  // Política: si la toma no trae Marca, se muestra como faltante. NO se infiere
  // desde URL/título en el frontend porque inventa data sin trazabilidad
  // (ver bug "Ver" matchea "in-ver-ter"). Lo correcto es atacarlo upstream
  // (scrapper / catalogador IA en backend). Ver FIX-11 en archive/FIXES_PENDIENTES.md.
  const brandIdentificationEnabled = false;
  
  // Estados para orden de columnas y preferencias de usuario
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  
  // Funciones para manejar preferencias de columnas en Firebase
  const saveColumnPreferences = useCallback(async (order: string[], visibility: VisibilityState) => {
    if (!currentUser?.uid) {
      console.warn('⚠️ No hay usuario autenticado, no se pueden guardar preferencias');
      return;
    }

    try {
      await saveColumnPreferencesFirebase(currentUser.uid, tableName, order, visibility);
    } catch (error) {
      console.error('Error guardando preferencias:', error);
    }
  }, [tableName, currentUser]);

  const loadColumnPreferences = useCallback(async () => {
    if (!currentUser?.uid) {
      console.warn('⚠️ No hay usuario autenticado, no se pueden cargar preferencias');
      return null;
    }

    try {
      const preferences = await loadColumnPreferencesFirebase(currentUser.uid, tableName);
      return preferences;
    } catch (error) {
      console.error('Error cargando preferencias:', error);
      return null;
    }
  }, [tableName, currentUser]);
  
  // Funciones para drag & drop de columnas
  const handleColumnDragStart = (columnId: string) => {
    setDraggedColumn(columnId);
  };
  
  const handleColumnDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  
  const handleColumnDrop = (targetColumnId: string) => {
    if (!draggedColumn || draggedColumn === targetColumnId) return;
    
    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Remover la columna de su posición actual
    newOrder.splice(draggedIndex, 1);
    // Insertarla en la nueva posición
    newOrder.splice(targetIndex, 0, draggedColumn);
    

    
    setColumnOrder(newOrder);
    setDraggedColumn(null);
  };
  
  const handleColumnDragEnd = () => {
    setDraggedColumn(null);
  };
  
  // Estados para el modal de imagen
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    title: string;
  } | null>(null);
  
  // Funciones para manejar el modal de imagen
  const openImageModal = (imageUrl: string, title: string) => {
    setSelectedImage({
      url: imageUrl,
      title: title
    });
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };
  
  // Estado del modal de exportación
  const [exportModalOpen, setExportModalOpen] = useState(false);
  
  // Estados para el progress toast
  const [exportProgress, setExportProgress] = useState(0);
  const [exportMessage, setExportMessage] = useState('');
  const [showExportProgress, setShowExportProgress] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  
  // Función para convertir texto a title case (cada palabra con primera letra mayúscula)
  const toTitleCase = (str: string): string => {
    if (!str) return str;
    
    return str
      .toLowerCase()
      .split(' ')
      .map(word => {
        // Mantener ciertas palabras en minúsculas (artículos, preposiciones, etc.)
        const lowercaseWords = ['de', 'del', 'la', 'el', 'las', 'los', 'y', 'o', 'para', 'con', 'en', 'sin', 'por'];
        if (lowercaseWords.includes(word) && word.length > 0) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ')
      // Capitalizar siempre la primera palabra
      .replace(/^[a-z]/, match => match.toUpperCase());
  };

  // Inicializar filtros desde props (si se necesita en el futuro)
  // Migrar preferencias de localStorage a Firebase (solo una vez por usuario)
  useEffect(() => {
    if (currentUser?.uid) {
      migrateLocalStorageToFirestore(currentUser.uid).catch(error => {
        console.error('Error en migración:', error);
      });
    }
  }, [currentUser]);

  useEffect(() => {
    // Aquí se puede agregar lógica para inicializar filtros si es necesario
  }, [tableName]);

  // Re-cargar datos cuando cambie el idioma
  useEffect(() => {
    // Los datos se re-renderizan automáticamente cuando cambia el idioma
    // No es necesario forzar un re-render manual
  }, [i18n.language]);
  
  // Guardar preferencias cuando cambia la visibilidad de columnas
  useEffect(() => {
    if (columnOrder.length > 0) {
      saveColumnPreferences(columnOrder, columnVisibility);
    }
  }, [columnVisibility, columnOrder, saveColumnPreferences]);
  
  // Reordenar columnas cuando cambia columnOrder
  useEffect(() => {
    if (columnOrder.length > 0 && columns.length > 0) {
      // Deduplicar columnOrder para evitar que IDs repetidos generen columnas duplicadas
      const seenOrderIds = new Set<string>();
      const uniqueColumnOrder = columnOrder.filter(id => {
        if (seenOrderIds.has(id)) return false;
        seenOrderIds.add(id);
        return true;
      });

      const reorderedColumns = uniqueColumnOrder
        .map((columnId: string) => columns.find(col => col.id === columnId))
        .filter((col): col is ColumnDef<TableData> => col !== undefined);

      // Solo actualizar si el orden realmente cambió
      const currentOrder = columns.map(col => col.id);
      const newOrder = reorderedColumns.map(col => col.id);

      if (JSON.stringify(currentOrder) !== JSON.stringify(newOrder)) {
        setColumns(reorderedColumns);
      }
    }
  }, [columnOrder]); // Removido 'columns' de las dependencias para evitar loop infinito
  

  // Wrapper que llama a la función extraída con el estado del componente
  const createComparisonColumns = async () => {
    await buildComparisonColumnsExternal({
      comparisonData,
      comparisonMode,
      data,
      selectedProducts,
      toggleSelectAll,
      toggleProductSelection,
      onRemoveFromComparison,
      openImageModal,
      enableSku,
      enableTags,
      isUnitPriceClient,
      tableName,
      expandedRows,
      isRowExpanded,
      handleBuyboxToggle,
      setLoadingVariants,
      handleVariantsLoaded,
      skuValuesRef,
      skusRef,
      editingSkuRef,
      handleSkuEditRef,
      handleSkuSaveRef,
      handleSkuCancelRef,
      handleSkuChangeRef,
      tagColumnsMeta,
      t,
      toTitleCase,
      getColumnDisplayName,
      loadColumnPreferences,
      setColumns,
      setColumnOrder,
      setColumnVisibility,
    });
  };
  

  
  // Lista de marcas de referencia para identificación automática
  // TODO(P2): reemplazar este array hardcoded por el `brandWhitelist` del
  // cliente activo (configurado en Firestore client config y consumido hoy
  // en el backend, ej. server/utils/catalogValidatorV2.js). El frontend
  // todavía no expone ese campo via ClientContext / useClient(); cuando se
  // agregue, leerlo desde el client context y usar este array solo como
  // fallback. Marcas como "ver", "lee" son substring traps y dependen de
  // que el match sea word-boundary (ver fix en extractBrandFromUrl).
  const BRAND_REFERENCE_LIST = [
    'adidas', 'nike', 'puma', 'reebok', 'new balance', 'converse', 'vans', 
    'fila', 'under armour', 'asics', 'skechers', 'crocs', 'timberland',
    'dc', 'quiksilver', 'roxy', 'billabong', 'volcom', 'hurley', "o'neill", 'oneill',
    'champion', 'kappa', 'umbro', 'le coq sportif', 'diadora',
    'polo ralph lauren', 'tommy hilfiger', 'calvin klein', 'lacoste',
    'hugo boss', 'armani', 'versace', 'gucci', 'prada', 'dolce gabbana',
    'levi', 'wrangler', 'lee', 'diesel', 'gstar', 'true religion',
    'patagonia', 'columbia', 'the north face', 'mountain hardwear',
    'salomon', 'merrell', 'keen', 'caterpillar', 'wolverine',
    'dr martens', 'clarks', 'ecco', 'geox', 'steve madden',
    'nine west', 'stuart weitzman', 'jimmy choo', 'christian louboutin',
    'manolo blahnik', 'tory burch', 'michael kors', 'coach', 'kate spade',
    'marc jacobs', 'alexander mcqueen', 'balenciaga', 'saint laurent',
    'givenchy', 'valentino', 'bottega veneta', 'burberry', 'hermès',
    // Marcas adicionales comunes en deportes
    'topper', 'jaguar', 'penalty', 'kappa', 'joma', 'mizuno', 'lotto',
    // Marcas argentinas y locales
    'revolver', 'legacy', 'mistral', 'montagne', 'ansilta', 'doite',
    'elepants', 'kevingston', 'bensimon', 'prune', 'kosiuko', 'rapsodia',
    'mishka', 'ayres', 'she', 'complot', 'sweet victorian', 'basement',
    'yagmour', 'kill', 'etiqueta negra', 'vitamina', 'ver', 'chocolate'
  ];
  
  // Paginación del servidor
  const [totalPages, setTotalPages] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [totalUnfilteredRows, setTotalUnfilteredRows] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100, // Aumentado a 100 para cargar más datos por página
  });

    // Cargar esquema de la tabla
  const loadTableSchema = async () => {
    
    // Validar que tableName existe antes de hacer la petición
    if (!tableName || tableName.trim() === '') {
      setError('Nombre de tabla no válido');
      return;
    }
    
    // Limpiar error anterior y mostrar carga
    setError(null);
    setLoading(true);

    const finishSchema = debugStep('loadTableSchema', { tableName });
    let finishSetupColumns: ((endMeta?: Record<string, unknown>) => void) | null = null;

    try {
      // Preview mode: fabricamos un schemaResponse sintético desde el sample
      // local + columnConfigOverride. Evita el fetch al endpoint del schema.
      const buildPreviewSchemaResponse = () => {
        const rows = Array.isArray(previewSampleRows) ? previewSampleRows : [];
        const allKeys = new Set<string>();
        rows.forEach(r => Object.keys(r || {}).forEach(k => allKeys.add(k)));
        const cfg = columnConfigOverride || null;

        // Generar tagColumns desde el config (mismo slug que usa el backend) +
        // garantizar que cada field exista en el row aunque sea como null.
        const synthTagColumns = cfg && cfg.tags
          ? Object.entries(cfg.tags)
              .filter(([_, e]: any) => e && e.enabled !== false)
              .map(([tagKey, e]: any) => ({
                field: `tag_${slugifyTagKey(tagKey)}`,
                tagKey,
                tagDisplay: e?.displayName || tagKey,
              }))
          : [];

        // Asegurar que cada field de tag esté en allKeys aunque la row no lo
        // traiga literal (ej. sample sintético con keys upper-case): hidratar
        // los rows desde claves alternativas (tag_<TAGKEY>, tag_<tagkey>, etc.).
        synthTagColumns.forEach(tc => {
          allKeys.add(tc.field);
          rows.forEach((r: any) => {
            if (!r) return;
            if (r[tc.field] !== undefined) return;
            const candidates = [
              `tag_${tc.tagKey}`,
              `tag_${String(tc.tagKey).toLowerCase()}`,
              `tag_${String(tc.tagKey).toUpperCase()}`,
            ];
            for (const k of candidates) {
              if (r[k] !== undefined) { r[tc.field] = r[k]; break; }
            }
          });
        });

        const synthSchema = Array.from(allKeys).map(key => ({
          Field: key,
          Type: typeof rows[0]?.[key] === 'number' ? 'decimal(15,2)' : 'varchar(255)',
          Null: 'YES', Key: '', Default: null, Extra: '',
        }));

        return {
          schema: synthSchema,
          metadata: {},
          tagColumns: synthTagColumns,
          columnConfig: cfg,
        };
      };

      const schemaResponse = previewMode
        ? buildPreviewSchemaResponse()
        : (await api.get(`/api/tables/${tableName}/schema`, {
            timeout: 10000,
            headers: { 'Content-Type': 'application/json' }
          })).data;
      let schema: any[] = [];
      let metadata: Record<string, any> = {};
      let tagColumnsFromResponse: TagColumnMeta[] = [];
      // Slice 4: columnConfig de la API (null si el cliente no tiene doc).
      // Si es null → comportamiento idéntico al actual (fallback completo).
      const columnConfigFromResponse: any =
        (schemaResponse && typeof schemaResponse === 'object' && (schemaResponse as any).columnConfig) || null;
      // Espejo el config en state para que el dropdown del engranaje lo vea.
      setColumnConfigSnapshot(columnConfigFromResponse);

      if (Array.isArray(schemaResponse)) {
        schema = schemaResponse;
        metadata = (schemaResponse as any)._metadata || {};
      } else if (schemaResponse && typeof schemaResponse === 'object') {
        schema = Array.isArray(schemaResponse.schema) ? schemaResponse.schema : [];
        metadata = schemaResponse.metadata || schemaResponse._metadata || {};
        if (Array.isArray(schemaResponse.tagColumns) || (schemaResponse.tagColumns && typeof schemaResponse.tagColumns === 'object')) {
          const rawTagColumns = Array.isArray(schemaResponse.tagColumns)
            ? schemaResponse.tagColumns
            : Object.values(schemaResponse.tagColumns as Record<string, any>);

          tagColumnsFromResponse = rawTagColumns
            .map((column: any) => ({
              field: String(column.field || '').trim(),
              tagKey: String(column.tagKey || '').trim(),
              tagDisplay: String(column.tagDisplay || column.tagKey || '').trim()
            }))
            .filter((column: TagColumnMeta) => column.field !== '' && column.tagKey !== '');
        }
      } else {
        schema = [];
        metadata = {};
      }

      // Ensure schema is always an array even if backend returned an object map
      if (!Array.isArray(schema)) {
        if (schema && typeof schema === 'object') {
          schema = Object.values(schema);
        } else {
          schema = [];
        }
      }

      if ((!metadata || Object.keys(metadata).length === 0) && (schema as any)?._metadata) {
        metadata = (schema as any)._metadata;
      }

      // Schema fetch + parse listos. Marcamos fin del paso schema y abrimos setupColumns.
      finishSchema({ columnCount: Array.isArray(schema) ? schema.length : 0, tagCount: tagColumnsFromResponse.length });
      finishSetupColumns = debugStep('setupColumns', { tableName });

      let normalizedTagColumns: TagColumnMeta[] = [];

      // Solo procesar tagColumns si enableTags es true
      if (enableTags) {
        const seenTagFields = new Set<string>();
        tagColumnsFromResponse.forEach((column: TagColumnMeta) => {
          if (!column.field || seenTagFields.has(column.field)) {
            return;
          }
          seenTagFields.add(column.field);
          normalizedTagColumns.push({
            field: column.field,
            tagKey: column.tagKey,
            tagDisplay: column.tagDisplay || column.tagKey
          });
        });
      }

      const tagsChanged =
        normalizedTagColumns.length !== tagColumnsMeta.length ||
        normalizedTagColumns.some((col: TagColumnMeta, index: number) => {
          const existing = tagColumnsMeta[index];
          return (
            !existing ||
            existing.field !== col.field ||
            existing.tagKey !== col.tagKey ||
            existing.tagDisplay !== col.tagDisplay
          );
        });

      if (tagsChanged) {
        setTagColumnsMeta(normalizedTagColumns);
      }

      const effectiveTagColumns = normalizedTagColumns;
      let _hasBuyboxData = metadata.hasBuyboxData || false;
      let _hasSellerData = metadata.hasSellerData || false;
      
      
      // Si no hay metadatos, detectar desde los datos reales (para modo demo)
      if (!metadata.hasBuyboxData && !metadata.hasSellerData && data.length > 0) {
        
        // Verificar si hay campo buybox_id en el esquema
        const hasBuyboxField = schema.some((col: any) => 
          col.Field.toLowerCase() === 'buybox_id' || col.Field === 'buybox_id'
        );
        
        // Verificar si hay campo seller en el esquema
        const hasSellerField = schema.some((col: any) => 
          col.Field.toLowerCase() === 'seller' || col.Field === 'Seller'
        );
        
        // Si los campos existen en el esquema, verificar si tienen datos válidos
        if (hasBuyboxField) {
          // Log algunos productos para verificar datos
          
          _hasBuyboxData = data.some((product: any) => {
            const buyboxId = product.buybox_id;
            return buyboxId !== null && 
                   buyboxId !== undefined && 
                   buyboxId !== '' && 
                   buyboxId !== '(NULL)' && 
                   buyboxId !== 'NULL' &&
                   buyboxId !== '0' &&
                   buyboxId !== 0;
          });
        }
        
        if (hasSellerField) {
          // Log algunos productos para verificar datos de seller
          
          _hasSellerData = data.some((product: any) => {
            const seller = product.Seller || product.seller;
            return seller !== null && 
                   seller !== undefined && 
                   seller !== '' && 
                   seller !== '(NULL)' && 
                   seller !== 'NULL';
          });
        }
        
      }
      
      
      // Filtrar columnas que no queremos mostrar
      const excludedColumns = ['id_request_group', 'fecha', 'id_catalogo'];
      const filteredSchema = schema.filter((column: any) =>
        !excludedColumns.includes(column.Field.toLowerCase())
      );

      // Definir el orden deseado de las columnas EXACTAMENTE como se solicita
      let baseColumnOrder = [
        'imagen',      // 1. Imagen
        'marca',       // 2. Marca
        'titulo',      // 3. Título
        'buybox',      // 4. BuyBox
        'seller',      // 5. Seller
        'precio',      // 6. Precio
        'precio_desc', // 7. Precio Desc.
        'precio_venta',// 8. Precio Venta
        'cuotas',      // 9. Cuotas
        'precio_cuota',// 10. Valor Cuota
        'categoria',   // 11. Categoría
        'sitio',       // 12. Sitio
        // 'tags',        // 13. Tags / In-Out - REMOVIDO
        'pagina_orden',// 14. Pag : Ord (combinado)
        'fecha',       // 15. Fecha de la toma (per-fila)
        'actions'      // 16. Acciones (comparar)
      ];

      if (isUnitPriceClient) {
        // Para Kimberly Clark: ocultar columnas por defecto (marca, categoria, precio_unidad, precio_unidad_medida)
        // porque se usan las versiones de tags dinámicas (MARCA, CATEGORIA, PU, PUM)
        baseColumnOrder = baseColumnOrder.filter(col => col !== 'categoria' && col !== 'marca');
      }

      if (isTiaClient) {
        // Si el cliente tiene columnConfig.native.{udm,contenido_total} declarado con mergeWithCatalog,
        // las columnas se inyectan vía la rama Slice 4 (líneas ~1222) y se renderizan con
        // MergedTomaCatalogCell + EditableCatalogCell. No las hardcodear acá para no pisar.
        const nativeHasUdm = !!columnConfigFromResponse?.native?.udm?.enabled;
        const nativeHasContenido = !!columnConfigFromResponse?.native?.contenido_total?.enabled;
        if (!nativeHasUdm || !nativeHasContenido) {
          const tituloIdx = baseColumnOrder.indexOf('titulo');
          const insertPos = tituloIdx >= 0 ? tituloIdx + 1 : baseColumnOrder.length;
          const toInsert: string[] = [];
          if (!nativeHasContenido) toInsert.push('contenido_total');
          if (!nativeHasUdm) toInsert.push('udm');
          baseColumnOrder = [
            ...baseColumnOrder.slice(0, insertPos),
            ...toInsert,
            ...baseColumnOrder.slice(insertPos)
          ];
        }
      }

      const tagFieldNames = effectiveTagColumns.map((column: TagColumnMeta) => column.field);
      let columnOrder = [...baseColumnOrder];
      if (tagFieldNames.length > 0) {
        const tagsIndex = columnOrder.indexOf('tags');
        if (tagsIndex >= 0) {
          columnOrder = [
            ...columnOrder.slice(0, tagsIndex + 1),
            ...tagFieldNames,
            ...columnOrder.slice(tagsIndex + 1)
          ];
        } else {
          const paginaIndex = columnOrder.indexOf('pagina_orden');
          if (paginaIndex >= 0) {
            columnOrder = [
              ...columnOrder.slice(0, paginaIndex),
              ...tagFieldNames,
              ...columnOrder.slice(paginaIndex)
            ];
          } else {
            columnOrder = [...columnOrder, ...tagFieldNames];
          }
        }
      }

      const desiredColumnOrder = [...columnOrder];

      // Agregar 'sku' al columnOrder si enableSku es true y no está presente
      if (enableSku && !desiredColumnOrder.includes('sku')) {
        // Insertar 'sku' después de 'imagen' (o al principio si no hay imagen)
        const imagenIndex = desiredColumnOrder.indexOf('imagen');
        if (imagenIndex >= 0) {
          desiredColumnOrder.splice(imagenIndex + 1, 0, 'sku');
        } else {
          desiredColumnOrder.unshift('sku');
        }
      }

      // Para Kimberly Clark: reordenar columnas con tags en posiciones específicas
      if (isUnitPriceClient && effectiveTagColumns.length > 0) {
        const findTagField = (tagKey: string) =>
          effectiveTagColumns.find((tc: TagColumnMeta) => tc.tagKey === tagKey)?.field;

        const marcaTag = findTagField('MARCA');
        const categoriaTag = findTagField('CATEGORIA');
        const subcategoriaTag = findTagField('SUBCATEGORIA');
        const puTag = findTagField('PU');
        const pumTag = findTagField('PUM');

        // Columnas que van al inicio en orden específico
        const priorityOrder = [
          'imagen', 'sku',
          marcaTag, categoriaTag, subcategoriaTag,
          'titulo', 'buybox', 'seller',
          'precio', 'precio_desc', 'precio_venta',
          puTag, pumTag,
        ].filter(Boolean) as string[];

        // Resto de columnas que no están en priorityOrder
        const remaining = desiredColumnOrder.filter(col => !priorityOrder.includes(col));
        desiredColumnOrder.length = 0;
        desiredColumnOrder.push(...priorityOrder, ...remaining);
      }

             // Ordenar las columnas según el orden especificado y agregar otras al final
       const orderedSchema: any[] = [];

             // Primero, agregar las columnas en el orden especificado
      // console.log('🔍 columnOrder actual:', desiredColumnOrder);
      // console.log('🔍 enableSku en este punto:', enableSku);
      desiredColumnOrder.forEach(columnName => {
        // Guard: respetar config de Core columns (admin desactivó esta columna)
        if (columnConfigFromResponse?.core?.[columnName]?.enabled === false) {
          return;
        }
        // console.log('🔍 Procesando columnName:', columnName);
        if ((columnName === 'precio_unidad' || columnName === 'precio_unidad_medida') && isUnitPriceClient) {
          orderedSchema.push({
            Field: columnName,
            Type: 'virtual',
            Null: 'YES',
            Key: '',
            Default: null,
            Extra: ''
          });
          return;
        }
        if ((columnName === 'contenido_total' || columnName === 'udm') && isTiaClient) {
          // Si el cliente declaró esta columna en native con mergeWithCatalog, la deja
          // que la inyecte la rama Slice 4 — NO hardcodear como virtual.
          const declaredInNative = !!columnConfigFromResponse?.native?.[columnName]?.enabled;
          if (declaredInNative) return;
          orderedSchema.push({
            Field: columnName,
            Type: 'virtual',
            Null: 'YES',
            Key: '',
            Default: null,
            Extra: ''
          });
          return;
        }
        // Columna virtual `fecha`: fecha de la toma (nativa `toma_*.fecha`, per-fila).
        // Se agrega SIEMPRE (todas las tomas traen `item.fecha` del endpoint /data).
        if (columnName === 'fecha') {
          orderedSchema.push({
            Field: 'fecha',
            Type: 'virtual',
            Null: 'YES',
            Key: '',
            Default: null,
            Extra: ''
          });
          // PT-#63: columna virtual `semana` (ISO 8601 `AAAA-Www`) pegada a Fecha,
          // derivada del MISMO item.fecha. Se agrega junto a fecha para garantizar
          // adyacencia sin depender del columnConfig per-cliente.
          orderedSchema.push({
            Field: 'semana',
            Type: 'virtual',
            Null: 'YES',
            Key: '',
            Default: null,
            Extra: ''
          });
          return;
        }
        const tagColumn = effectiveTagColumns.find((column: TagColumnMeta) => column.field === columnName);
        if (tagColumn) {
          orderedSchema.push({
            Field: tagColumn.field,
            Type: 'virtual',
            Null: 'YES',
            Key: '',
            Default: null,
            Extra: '',
            tagKey: tagColumn.tagKey,
            tagDisplay: tagColumn.tagDisplay
          });
          return;
        }

        if (columnName === 'buybox') {
          // Siempre agregar columna de BuyBox si los campos existen
          const hasBuyboxFields = filteredSchema.some((col: any) => 
            col.Field === 'buybox_id' || col.Field === 'buybox_winner'
          );
          if (hasBuyboxFields) {
            orderedSchema.push({
              Field: 'buybox',
              Type: 'virtual',
              Null: 'YES',
              Key: '',
              Default: null,
              Extra: ''
            });
          } else {
          }
        } else if (columnName === 'seller') {
          // Solo agregar columna de Seller si hay datos válidos
          const sellerCol = filteredSchema.find((col: any) => {
            const fieldLower = col.Field.toLowerCase();
            return fieldLower === 'seller' || fieldLower === 'vendedor';
          });
          if (sellerCol) {
            orderedSchema.push(sellerCol);
          } else {
          }
        } else if (columnName === 'sku') {
          // Agregar columna virtual SKU editable si enableSku es true
          if (enableSku) {
            orderedSchema.push({
              Field: 'sku',
              Type: 'virtual',
              Null: 'YES',
              Key: '',
              Default: null,
              Extra: ''
            });
          }
        } else if (columnName === 'pagina_orden') {
          // Las posiciones ahora vienen del campo apariciones
          const aparicionesCol = filteredSchema.find((col: any) => {
            return col.Field.toLowerCase() === 'apariciones';
          });

          if (aparicionesCol) {
            // Crear una columna virtual
            orderedSchema.push({
              Field: 'pagina_orden',
              Type: 'varchar(255)',
              Null: 'YES',
              Key: '',
              Default: null,
              Extra: '',
              OriginalAparicionesField: aparicionesCol.Field
            });
          } else {
            console.warn('⚠️ No se encontró campo apariciones en el schema');
          }
        } else {
          // Buscar la columna por nombre exacto o variantes comunes
          let column = filteredSchema.find((col: any) => col.Field.toLowerCase() === columnName);
          
          // Si no se encuentra, buscar variantes comunes según el nombre esperado
          if (!column) {
            switch (columnName) {
              case 'precio':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'precio' || fieldLower === 'original_price' || fieldLower === 'price' || fieldLower.includes('precio');
                });
                break;
              case 'precio_desc':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'precio_desc' || fieldLower === 'discounted_price' || fieldLower === 'precio_descuento';
                });
                break;
              case 'precio_venta':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'precio_venta' || fieldLower === 'sale_price' || fieldLower === 'precioventa';
                });
                break;
              case 'cuotas':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'cuotas' || fieldLower === 'installments' || fieldLower === 'installements' || fieldLower.includes('cuota');
                });
                break;
              case 'precio_cuota':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'precio_cuota' || fieldLower === 'installements_value' || fieldLower === 'valor_cuota' || 
                         fieldLower.includes('installements_value') || fieldLower.includes('valor') || fieldLower.includes('cuota');
                });
                break;
              case 'marca':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'marca' || fieldLower.includes('marca') || fieldLower.includes('brand');
                });
                break;
              case 'titulo':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'titulo' || fieldLower === 'title' || fieldLower.includes('titulo') || fieldLower.includes('title');
                });
                break;
              case 'imagen':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'imagen' || fieldLower === 'image' || fieldLower.includes('imagen') || fieldLower.includes('image');
                });
                break;
              case 'categoria':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'categoria' || fieldLower === 'category' || fieldLower === 'cat';
                });
                break;
              case 'tags':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'tags' ||
                         fieldLower === 'tag' ||
                         fieldLower === 'tag_status' ||
                         fieldLower === 'estado_in_out' ||
                         fieldLower === 'estadoinout' ||
                         fieldLower.includes('tag') ||
                         fieldLower.includes('in_out') ||
                         fieldLower.includes('inout') ||
                         fieldLower === 'estado';
                });
                break;
              case 'sitio':
                column = filteredSchema.find((col: any) => {
                  const fieldLower = col.Field.toLowerCase();
                  return fieldLower === 'sitio' || fieldLower === 'site' || fieldLower.includes('pagina') || fieldLower.includes('page') || fieldLower.includes('url');
                });
                break;
            }
          }
          
          if (column) {
            // Si encontramos la columna con un nombre diferente, crear un alias
            if (column.Field.toLowerCase() !== columnName) {
              orderedSchema.push({
                ...column,
                Field: columnName, // Usar el nombre esperado
                OriginalField: column.Field // Guardar el nombre original
              });
            } else {
              // PT-#69: aunque el match sea case-insensitive exitoso, el DB Field puede tener
              // casing distinto al id canónico (ej. `Marca` vs `marca`). El leaf id de TanStack
              // hereda ese casing → resolveTemplateOrder (case-sensitive) no matchea el item
              // `marca` del template y la columna se appendea al final del export PUBS. Normalizamos
              // el Field al id canónico lowercase (columnName) y preservamos OriginalField para
              // que el acceso a datos siga leyendo row[Field original]. Single source of id.
              if (column.Field !== columnName) {
                orderedSchema.push({
                  ...column,
                  Field: columnName,
                  OriginalField: column.Field,
                });
              } else {
                orderedSchema.push(column);
              }
            }

          }
        }
      });
      
              // NO agregar columnas adicionales - solo usar las del orden especificado
        // Esta sección se comenta para mantener SOLO el orden especificado
        /*
        filteredSchema.forEach((column: any) => {
         const fieldName = column.Field.toLowerCase();
         const isPageColumn = fieldName.includes('pagina') || fieldName.includes('page');
         const isOrderColumn = fieldName.includes('orden') || fieldName.includes('order');
         const isSiteColumn = fieldName.includes('site');

         // Verificar si la columna ya fue agregada en orderedSchema
         const alreadyAdded = orderedSchema.some(col => col.Field.toLowerCase() === fieldName);

         // Excluir columnas que ya se manejan de forma especial o que ya fueron agregadas
         if (!columnOrder.includes(fieldName) &&
             !isPageColumn &&
             !isOrderColumn &&
             !isSiteColumn &&
             !alreadyAdded) {
           orderedSchema.push(column);
         }
       });
       */

      // === Slice 4: Inyección Native columns desde columnConfig (aditivo) ===
      // Solo si el cliente tiene el doc Firestore. Sin doc → comportamiento idéntico actual.
      // Aditivo: solo agrega columnas que NO están en orderedSchema todavía.
      if (columnConfigFromResponse && columnConfigFromResponse.native) {
        const nativeEntries = Object.entries(columnConfigFromResponse.native)
          .filter(([_, e]: any) => e && e.enabled === true)
          .sort(([_, a]: any, [__, b]: any) => ((a?.order ?? 9999) - (b?.order ?? 9999)));
        // Campos catalog-only: viven en el JOIN a `catalog`, NUNCA como columna
        // física de la toma. Una Native que declara `mergeWithCatalog` apuntando a
        // uno de estos NO debe exigir que su sourceField exista en el schema físico
        // (el accessorFn/renderer leen SOLO del catalog — sourceField es vestigial).
        const CATALOG_ONLY_MERGE_FIELDS = ['catalog_brand', 'catalog_category', 'subcategory', 'segmento', 'contenido', 'udm', 'sku_origen', 'sku_unificado', 'seller'];
        for (const [colId, entryRaw] of nativeEntries) {
          const entry = entryRaw as any;
          const sourceField = entry.sourceField || colId;
          // Verificar que el field exista en filteredSchema (de DESCRIBE).
          // Las Native con `mergeWithCatalog` sobre una columna física (ej.
          // segment→segmento) siguen exigiendo sourceField. PERO si el merge apunta
          // a un campo catalog-only (subcategory, etc.), el sourceField físico es
          // vestigial: se sintetiza un schema entry virtual y la columna se agrega
          // igual para renderizar desde catalog.
          const isCatalogOnlyMerge = !!entry.mergeWithCatalog &&
            CATALOG_ONLY_MERGE_FIELDS.includes(String(entry.mergeWithCatalog));
          const exists = filteredSchema.some((c: any) => c.Field === sourceField);
          if (!exists && !isCatalogOnlyMerge) continue;
          // No duplicar — solo skip si MISMO id custom ya existe en orderedSchema.
          // Permitir que dos columnas distintas apunten al mismo sourceField
          // (ej. virtual sistema "sku" + Native "sku_db" con sourceField "sku").
          const already = orderedSchema.some((c: any) => c.Field === colId);
          if (already) continue;
          orderedSchema.push({
            Field: colId,
            Type: 'native',
            Null: 'YES',
            Key: '',
            Default: null,
            Extra: '',
            sourceField,
            columnConfigEntry: entry,
          });
        }
      }

      // Agregar columna de acciones SIEMPRE (se mostrará diferente según el modo)
      
      orderedSchema.push({
        Field: 'actions',
        Type: 'virtual',
        Null: 'NO',
        Key: '',
        Default: null,
        Extra: ''
      });
      
      // Log final de columnas ordenadas
      // console.log('📋 orderedSchema final:', orderedSchema.map((col: any) => col.Field));

      let tableColumns: ColumnDef<TableData>[] = orderedSchema.map((column: any, index: number) => {

        const columnField = column.Field.toLowerCase();

        // Slice 4: ColumnDef para Native columns inyectadas desde columnConfig.
        // Usa GenericCell con format declarado en el config.
        if (column.columnConfigEntry) {
          const entry = column.columnConfigEntry as any;
          const sourceField = column.sourceField || column.Field;
          const headerLabel = entry.displayName || column.Field;
          const styleClass = getStylePresetClassName(entry.styleId);

          // Merge silencioso toma↔catalog para Native: si la entry tiene
          // `mergeWithCatalog`, render con MergedTomaCatalogCell (la columna sigue
          // siendo la misma, sólo cambia el renderer).
          if (entry.mergeWithCatalog) {
            const catalogField = String(entry.mergeWithCatalog);
            const validFields = ['catalog_brand', 'catalog_category', 'subcategory', 'segmento', 'contenido', 'udm', 'sku_origen', 'sku_unificado', 'seller'];
            if (validFields.includes(catalogField)) {
              const editableEnabled = !!(entry.editable && canEditCatalog && clientId);
              return {
                id: column.Field,
                // catalog = single source of truth cuando la columna declara
                // mergeWithCatalog. accessorFn devuelve SOLO el valor del catalog
                // (null si vacío) — NO hace fallback a toma. Esto garantiza
                // consistencia entre filter (bucket "(vacío)"), sort, display
                // (MergedTomaCatalogCell con catalogAuthoritative=true) y export
                // XLSX (que también lee accessorFn).
                accessorFn: (row: any) => {
                  const catalogVal = row?.[catalogField];
                  if (catalogVal != null && String(catalogVal).trim() !== '') return catalogVal;
                  return null;
                },
                header: headerLabel,
                cell: (info: any) => {
                  const rowData = info.row.original;
                  const merged = (
                    <MergedTomaCatalogCell
                      row={rowData}
                      tomaKey={sourceField}
                      catalogField={catalogField}
                      catalogAuthoritative={true}
                    />
                  );
                  const display = styleClass ? <span className={styleClass}>{merged}</span> : merged;
                  if (editableEnabled) {
                    const initial = rowData?.[catalogField] ?? rowData?.[sourceField];
                    // Bug 2: contenido es DECIMAL(15,3) → strip trailing zeros también al editar.
                    let initialStr = initial == null ? '' : String(initial);
                    if (catalogField === 'contenido' && initialStr !== '') {
                      const n = Number(initialStr);
                      if (Number.isFinite(n)) initialStr = n.toString();
                    }
                    return (
                      <EditableCatalogCell
                        row={rowData}
                        clientId={clientId!}
                        field={catalogField as CatalogField}
                        display={display}
                        initialValue={initialStr}
                      />
                    );
                  }
                  return display;
                },
                // BUG-009 fix: accessorFn devuelve scalar (catalog override → toma → null),
                // por lo que TanStack puede ordenar con sort string default.
                enableSorting: true,
                enableColumnFilter: true,
                size: 160,
              };
            }
            // catalogField inválido → fallback a GenericCell normal abajo.
          }

          const fmt: ColumnFormat = {
            format: entry.format || 'text',
            decimals: entry.decimals,
            thousandsSeparator: entry.thousandsSeparator,
            currencySymbol: entry.currencySymbol,
          };
          return {
            id: column.Field,
            accessorFn: (row: any) => row[sourceField],
            header: headerLabel,
            cell: (info: any) => (
              styleClass
                ? <span className={styleClass}><GenericCell value={info.getValue()} config={fmt} /></span>
                : <GenericCell value={info.getValue()} config={fmt} />
            ),
            enableSorting: true,
            enableColumnFilter: true,
            size: 140,
          };
        }

        if (column.tagKey) {
          const isPriceTag = isUnitPriceClient && (column.tagKey === 'PU' || column.tagKey === 'PUM');
          const isBrandTag = isUnitPriceClient && column.tagKey === 'MARCA';
          const tagStyleId = columnConfigFromResponse?.tags?.[column.tagKey]?.styleId;
          const tagStyleClass = getStylePresetClassName(tagStyleId);

          let cellRenderer: any;
          if (isPriceTag && !tagStyleClass) {
            cellRenderer = ({ getValue }: { getValue: () => any }) => {
              const value = getValue();
              const numValue = parseNumericValue(value);
              return renderCalculatedPriceBadge(numValue);
            };
          } else if (isBrandTag && !tagStyleClass) {
            cellRenderer = ({ getValue }: { getValue: () => any }) => {
              const value = getValue();
              if (value === null || value === undefined || value === '' || value === 'null') {
                return <span className="text-gray-400">-</span>;
              }
              return (
                <div className="text-center">
                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                    {toTitleCase(String(value))}
                  </span>
                </div>
              );
            };
          } else {
            cellRenderer = ({ getValue }: { getValue: () => any }) => {
              const value = getValue();
              if (value === null || value === undefined || value === '') {
                return <span className="text-gray-400">-</span>;
              }
              const display = isBrandTag ? toTitleCase(String(value)) : String(value);
              if (tagStyleClass) {
                return <span className={tagStyleClass}>{display}</span>;
              }
              return <span className="text-sm text-gray-700">{display}</span>;
            };
          }

          return {
            id: column.Field,
            accessorKey: column.Field,
            header: column.tagDisplay || column.tagKey,
            cell: cellRenderer,
            enableSorting: true,
            enableColumnFilter: true,
            size: isBrandTag ? 150 : 160
          };
        }
        
        // Columna Fecha (virtual): fecha de la toma per-fila (`item.fecha`, ISO).
        // Formatea a DD/MM/YYYY con el MISMO helper que el export (formatFecha),
        // devuelve '' en null/vacía/inválida → celda vacía. tabular-nums por
        // convención del proyecto para números/fechas.
        if (column.Field === 'fecha') {
          return {
            id: 'fecha',
            accessorFn: (row: TableData) => (row as any).fecha ?? (row as any).Fecha ?? (row as any).FECHA ?? null,
            header: 'Fecha',
            cell: (info: any) => {
              const formatted = formatFecha(info.getValue());
              if (!formatted) {
                return <span className="text-gray-400">-</span>;
              }
              return <span className="text-sm text-gray-700 tabular-nums">{formatted}</span>;
            },
            enableSorting: true,
            enableColumnFilter: true,
            size: 110,
          };
        }

        // Columna Semana (virtual, PT-#63): número de semana ISO 8601 `AAAA-Www`
        // derivado del MISMO item.fecha vía getSemanaIso. '' → celda vacía. tabular-nums.
        if (column.Field === 'semana') {
          return {
            id: 'semana',
            accessorFn: (row: TableData) => (row as any).fecha ?? (row as any).Fecha ?? (row as any).FECHA ?? null,
            header: 'Semana',
            cell: (info: any) => {
              const semana = getSemanaIso(info.getValue());
              if (!semana) {
                return <span className="text-gray-400">-</span>;
              }
              return <span className="text-sm text-gray-700 tabular-nums">{semana}</span>;
            },
            enableSorting: true,
            enableColumnFilter: true,
            size: 100,
          };
        }

        // Columna de BuyBox
        if (column.Field === 'buybox') {
          return {
            id: 'buybox',
            header: t('columns.buybox'),
            cell: ({ row }: { row: any }) => {
              const rawBuyboxId = row.original.buybox_id;
              const rawBuyboxWinner = row.original.buybox_winner;
              const rawSeller = row.original.Seller || row.original.seller || '';
              
              // Debug log para los primeros productos
              if ((window as any).buyboxDebugCount < 10) {
                if (!(window as any).buyboxDebugCount) (window as any).buyboxDebugCount = 0;
                (window as any).buyboxDebugCount++;
              }
              
              // Verificar si buybox_id es realmente NULL o tiene un valor válido
              const isValidBuyboxId = rawBuyboxId !== null && 
                                    rawBuyboxId !== undefined && 
                                    rawBuyboxId !== '' && 
                                    rawBuyboxId !== '(NULL)' && 
                                    rawBuyboxId !== 'NULL' &&
                                    rawBuyboxId !== '0' &&
                                    rawBuyboxId !== 0;
              
              const buyboxId = isValidBuyboxId ? rawBuyboxId : null;
              // Manejar diferentes formatos de buybox_winner incluyendo NULL
              const isBuyboxWinner = rawBuyboxWinner === 1 || 
                                   rawBuyboxWinner === '1' || 
                                   rawBuyboxWinner === true ||
                                   rawBuyboxWinner === 'true' ||
                                   parseInt(rawBuyboxWinner) === 1;
              
              // Log para debug - solo para los primeros 5 productos con buybox_id
              if (buyboxId && (window as any).buyboxLogCount < 5) {
                if (!(window as any).buyboxLogCount) (window as any).buyboxLogCount = 0;
                (window as any).buyboxLogCount++;
              }
              
              // Validación adicional: solo considerar válidos los IDs numéricos
              if (buyboxId && !/^\d+$/.test(String(buyboxId))) {
                return null;
              }
              
              // Solo mostrar si tiene buybox_id Y es winner
              if (!buyboxId || !isBuyboxWinner) {
                return null;
              }
              
              const productId = row.original.id_catalogo || row.original.ID_CATALOGO || row.original.id;

              // Debug log for first product
              if (productId === '666324') {
              }
              
              // En modo comparación, usar el originalTableName del producto
              const effectiveTableName = comparisonMode && row.original.originalTableName 
                ? row.original.originalTableName 
                : tableName;
              
              
              return (
                <BuyBoxIndicator
                  isBuyboxWinner={isBuyboxWinner}
                  buyboxId={buyboxId}
                  seller={rawSeller}
                  isExpanded={isRowExpanded(productId)}
                  onToggle={() => {
                    handleBuyboxToggle(productId);
                    // Always set loading when toggling
                    if (!expandedRows.has(productId)) {
                      setLoadingVariants(prev => new Set(prev).add(productId));
                    }
                  }}
                  showExpandButton={true}
                  tableName={effectiveTableName}
                  onVariantsLoaded={(variants: any[]) => handleVariantsLoaded(productId, variants)}
                />
              );
            },
            enableSorting: false,
            enableColumnFilter: false,
            size: 100,
          };
        }

        // Columna de precios calculados (virtuales)
        if (isUnitPriceClient && column.Field === 'precio_unidad') {
          return {
            id: 'precio_unidad',
            accessorFn: (row: TableData) => row.precio_unidad ?? row.precio_unidad_calc ?? null,
            header: t('columns.precio_unidad'),
            cell: ({ row }: { row: any }) => {
              const salePrice = getSalePriceValue(row.original);
              const multiplier = getMultiplierValue(row.original, MPU_KEYS);
              const precomputed = parseNumericValue(row.original.precio_unidad ?? row.original.precio_unidad_calc);
              const computed =
                precomputed !== null
                  ? precomputed
                  : salePrice !== null && multiplier !== null
                    ? salePrice * multiplier
                    : null;
              return renderCalculatedPriceBadge(computed, formatMultiplierLabel('MPU', multiplier));
            },
            enableSorting: true,
            enableColumnFilter: false,
            size: 160,
          };
        }

        if (isUnitPriceClient && column.Field === 'precio_unidad_medida') {
          return {
            id: 'precio_unidad_medida',
            accessorFn: (row: TableData) => row.precio_unidad_medida ?? row.precio_unidad_medida_calc ?? null,
            header: t('columns.precio_unidad_medida'),
            cell: ({ row }: { row: any }) => {
              const salePrice = getSalePriceValue(row.original);
              const multiplier = getMultiplierValue(row.original, MPUM_KEYS);
              const precomputed = parseNumericValue(row.original.precio_unidad_medida ?? row.original.precio_unidad_medida_calc);
              const computed =
                precomputed !== null
                  ? precomputed
                  : salePrice !== null && multiplier !== null
                    ? salePrice * multiplier
                    : null;
              return renderCalculatedPriceBadge(computed, formatMultiplierLabel('MPUM', multiplier));
            },
            enableSorting: true,
            enableColumnFilter: false,
            size: 190,
          };
        }

        // Columnas GRAMAJE para TIA Ecuador
        if (isTiaClient && column.Field === 'contenido_total') {
          return {
            id: 'contenido_total',
            accessorFn: (row: TableData) => row.contenido_total ?? null,
            header: t('columns.contenido_total'),
            cell: ({ row }: { row: any }) => {
              const val = row.original.contenido_total;
              if (val === null || val === undefined || val === '') {
                return <span className="text-gray-400">—</span>;
              }
              return <span className="text-sm text-gray-800">{String(val)}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
            size: 110,
          };
        }

        if (isTiaClient && column.Field === 'udm') {
          return {
            id: 'udm',
            accessorFn: (row: TableData) => row.udm ?? null,
            header: t('columns.udm'),
            cell: ({ row }: { row: any }) => {
              const val = row.original.udm;
              if (val === null || val === undefined || val === '') {
                return <span className="text-gray-400">—</span>;
              }
              return <span className="text-sm text-gray-800">{String(val)}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
            size: 80,
          };
        }

        // Columna SKU editable
        if (column.Field === 'sku') {
          return {
            id: 'sku',
            header: t('columns.sku'),
            accessorKey: 'id_catalogo', // Solo para que la columna tenga una key
            cell: ({ row }: { row: any }) => {
              const id_catalogo = row.original.id_catalogo || row.original.ID_CATALOGO || row.original.id;
              if (!id_catalogo) {
                return <span className="text-sm text-gray-400">-</span>;
              }
              const isEditing = editingSkuRef.current === id_catalogo;
              const currentSkuValue = skuValuesRef.current?.[id_catalogo];
              const persistedSkuValue = skusRef.current?.[id_catalogo];
              // Fallback a catalog_sku materializado (enrichment) cuando no hay SKU editado/persistido.
              const catalogSkuValue = row.original.catalog_sku;
              const value = currentSkuValue ?? persistedSkuValue ?? catalogSkuValue ?? '';

              if (isEditing) {
                return (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => {
                        handleSkuChangeRef.current?.(id_catalogo, e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSkuSaveRef.current?.(id_catalogo);
                        } else if (e.key === 'Escape') {
                          handleSkuCancelRef.current?.(id_catalogo);
                        }
                      }}
                      className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      autoFocus
                      placeholder="Ingrese SKU"
                    />
                    <button
                      onClick={() => handleSkuSaveRef.current?.(id_catalogo)}
                      className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                      title="Guardar"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleSkuCancelRef.current?.(id_catalogo)}
                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                      title="Cancelar"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              }

              return (
                <div
                  className="flex items-start justify-between group cursor-pointer hover:bg-gray-50 px-2 py-0.5 rounded transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSkuEditRef.current?.(id_catalogo);
                  }}
                >
                  <span className={`text-sm leading-tight ${value ? 'text-gray-700' : 'text-gray-400'}`}>
                    {value || 'Click para editar'}
                  </span>
                  <button
                    className="opacity-0 group-hover:opacity-100 p-1 text-blue-600 hover:text-blue-800 transition-opacity"
                    title="Editar SKU"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </div>
              );
            },
            enableSorting: true,
            enableColumnFilter: false,
            size: 180,
          };
        }

        if (column.Field === 'tags') {
          const sourceField = (column as any).OriginalField || column.Field;
          return {
            id: 'tags',
            accessorFn: (row: TableData) => row[sourceField],
            header: t('columns.tags'),
            cell: ({ row }: { row: any }) => {
              const rawValue = row.original?.[sourceField];
              const parsedTags = parseRawTags(rawValue);

              if (parsedTags.length === 0) {
                return <span className="text-gray-400">-</span>;
              }

              return (
                <div className="flex flex-wrap gap-1 max-w-xl">
                  {parsedTags.map((tag, index) => (
                    <span
                      key={`${tag.key ?? 'value'}-${tag.value}-${index}`}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200"
                    >
                      {tag.key ? (
                        <>
                          <span className="font-semibold uppercase tracking-wide text-purple-600">{tag.key}</span>
                          <span className="mx-1 text-purple-300">:</span>
                          <span className="text-purple-800">{tag.value}</span>
                        </>
                      ) : (
                        <span className="text-purple-700">{tag.value}</span>
                      )}
                    </span>
                  ))}
                </div>
              );
            },
            enableSorting: false,
            enableColumnFilter: false,
            size: 320,
          };
        }
        
        // Columna de acciones para comparar
        if (column.Field === 'actions') {

          
          if (comparisonMode) {
            // Modo comparación - botón para remover
            return {
              id: 'actions',
              header: '',
              cell: ({ row }: { row: any }) => {
                const product = row.original;
                // Usar comparisonId directamente ya que es el identificador único
                const productId = product.comparisonId;
                
                return (
                  <div className="flex justify-center">
                    <button
                      onClick={() => {
                        onRemoveFromComparison?.(productId);
                      }}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remover de comparación"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              },
              enableSorting: false,
              enableColumnFilter: false,
              size: 80,
            };
          } else {
            // Modo normal - botón para agregar a comparación
            return {
              id: 'actions',
              header: '',
              cell: ({ row }: { row: any }) => {
                const product = row.original;

                
                // Verificar si el producto ya está en comparación
                const isInComparison = isProductInComparison(product);
                
                return (
                  <div className="flex justify-center items-center gap-1">
                    <button
                      onClick={() => {
                        if (onAddToComparison) {
                          onAddToComparison(product);
                        }
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        isInComparison
                          ? 'text-purple-700 hover:text-purple-900 hover:bg-purple-100 bg-purple-200'
                          : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50'
                      }`}
                      title={isInComparison ? "Ya está en comparación" : "Agregar a comparación"}

                    >
                      <GitCompare className={`h-4 w-4 ${isInComparison ? 'text-purple-700' : ''}`} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDataIssuesModalRow(product);
                      }}
                      className="p-2 rounded-lg transition-colors text-gray-400 hover:text-pink-600 hover:bg-pink-50"
                      title="Reportar issue / proponer edición"
                    >
                      <Flag className="h-4 w-4" />
                    </button>
                  </div>
                );
              },
              enableSorting: false,
              enableColumnFilter: false,
              size: 120,
            };
          }
        }
        
        // Para precio descontado, incluir información de descuento
        
        if (column.Field === 'precio_desc') {
          // 
          return {
            id: column.Field,
            accessorFn: (row: TableData) => {
              const fieldValue = column.OriginalField ? row[column.OriginalField] : row[column.Field];
              
              // Buscar el precio original en la misma fila para calcular descuento
              const precioOriginal = row.precio || row.original_price || row.PRECIO || row.price || row.Price || row.ORIGINAL_PRICE;
              
              if (fieldValue && precioOriginal && !isNaN(parseFloat(fieldValue)) && !isNaN(parseFloat(precioOriginal))) {
                const originalPrice = parseFloat(precioOriginal);
                const discountedPrice = parseFloat(fieldValue);
                const discount = originalPrice - discountedPrice;
                const discountPercentage = ((discount / originalPrice) * 100);
                
                let discountInfo = '';
                if (discount > 0) {
                  discountInfo = `Descuento: ${formatCurrencyValue(discount)} (${discountPercentage.toFixed(1)}%)`;
                } else if (discount < 0) {
                  discountInfo = `Recargo: +${formatCurrencyValue(Math.abs(discount))} (${Math.abs(discountPercentage).toFixed(1)}%)`;
                } else {
                  discountInfo = 'Mismo precio que original';
                }
                
                return {
                  value: fieldValue,
                  discountInfo: discountInfo
                };
              }
              
              return fieldValue;
            },
            header: columnConfigFromResponse?.core?.[column.Field]?.displayName || getColumnDisplayName(column.Field),
            cell: ({ getValue }: { getValue: () => any }) => {
              const value = getValue();
              
              // Para precio descontado, extraer el valor numérico si es un objeto
              let actualValue = value;
              let discountTooltip = '';
              
              if (value && typeof value === 'object' && value.value) {
                actualValue = value.value;
                discountTooltip = value.discountInfo || '';
              }
              
              const numericValue = parseFloat(actualValue);
              if (!isNaN(numericValue) && numericValue > 0) {
                // Formatear precio como $xxxx.yy
                const formattedPrice = formatCurrencyValue(numericValue);
                
                return (
                  <div className="text-center">
                    <span
                      className="font-bold text-gray-700 bg-gray-200 px-2 py-1 rounded-md whitespace-nowrap inline-block"
                      title={discountTooltip}
                    >
                      {formattedPrice}
                    </span>
                  </div>
                );
              }
              
              // Precio desc - no mostrar null o valores vacíos
              if (!value || value === 'null' || value === null || (value && typeof value === 'object' && !value.value)) {
                return <span className="text-gray-400">-</span>;
              }
              
              return String(value);
            },
            enableSorting: true,
            enableColumnFilter: false,
            size: undefined,
          };
        }

        // === Merge silencioso toma↔catalog para columnas Core ===========================
        // Si el admin configuró columnConfig.core[column.Field].mergeWithCatalog, esta
        // columna existente se renderiza con MergedTomaCatalogCell. NO se crea una
        // columna nueva — sólo se reemplaza el cell renderer.
        // Lookup case-insensitive: el column.Field puede venir capitalizado de DB
        // ("Marca", "Cat") mientras el column-config guarda lowercase ("marca", "cat").
        const coreEntries: any = columnConfigFromResponse?.core || {};
        const coreFieldLower = String(column.Field).toLowerCase();
        const coreCfg: any =
          coreEntries[column.Field] ||
          coreEntries[coreFieldLower] ||
          (() => {
            const matchKey = Object.keys(coreEntries).find(k => k.toLowerCase() === coreFieldLower);
            return matchKey ? coreEntries[matchKey] : undefined;
          })();
        if (coreCfg && coreCfg.mergeWithCatalog) {
          const tomaKey = column.OriginalField || column.Field;
          const catalogField = String(coreCfg.mergeWithCatalog);
          const headerLabel = coreCfg.displayName || getColumnDisplayName(column.Field);
          const styleClass = getStylePresetClassName(coreCfg.styleId);
          const isEditableField = (VALID_CATALOG_FIELDS as readonly string[]).includes(catalogField);
          const editableEnabled = !!(coreCfg.editable && isEditableField && canEditCatalog && clientId);
          return {
            id: column.Field,
            // catalog = single source of truth cuando la columna Core declara
            // mergeWithCatalog. accessorFn devuelve SOLO el valor del catalog
            // (null si vacío) — NO hace fallback a toma. Esto garantiza
            // consistencia entre filter (bucket "(vacío)"), sort, display
            // (MergedTomaCatalogCell con catalogAuthoritative=true) y export
            // XLSX (que también lee accessorFn).
            accessorFn: (row: any) => {
              const catalogVal = row?.[catalogField];
              if (catalogVal != null && String(catalogVal).trim() !== '') return catalogVal;
              return null;
            },
            header: headerLabel,
            cell: (info: any) => {
              const rowData = info.row.original;
              const merged = (
                <MergedTomaCatalogCell
                  row={rowData}
                  tomaKey={tomaKey}
                  catalogField={catalogField}
                  catalogAuthoritative={true}
                />
              );
              const display = styleClass ? <span className={styleClass}>{merged}</span> : merged;
              if (editableEnabled) {
                const initial = rowData?.[catalogField] ?? rowData?.[tomaKey];
                return (
                  <EditableCatalogCell
                    row={rowData}
                    clientId={clientId!}
                    field={catalogField as CatalogField}
                    display={display}
                    initialValue={initial == null ? '' : String(initial)}
                  />
                );
              }
              return display;
            },
            // BUG-009 fix: accessorFn devuelve scalar (catalog override → toma → null),
            // por lo que TanStack puede ordenar con sort string default.
            enableSorting: true,
            enableColumnFilter: true,
            size: 160,
          };
        }

        return {
          id: column.Field,
          accessorFn: (row: TableData) => {
            // Para columnas con alias (nombre original diferente), usar el nombre original
            if (column.OriginalField && column.Field !== column.OriginalField) {
              return row[column.OriginalField];
            }
            // Para la columna de sitio, usar el nombre original
            if (column.Field === 'sitio' && column.OriginalField) {
              return row[column.OriginalField];
            }
            // Para la columna combinada, parsear apariciones
            if (column.Field === 'pagina_orden') {
              // Parsear el campo apariciones
              let apariciones: any[] = [];
              const aparicionesValue = row[column.OriginalAparicionesField] || row.apariciones || row.Apariciones;

              // Debug para ver qué campos están disponibles
              if (!aparicionesValue) {
              }

              if (aparicionesValue) {
                try {
                  apariciones = typeof aparicionesValue === 'string'
                    ? JSON.parse(aparicionesValue)
                    : aparicionesValue;
                  apariciones = Array.isArray(apariciones) ? apariciones : [];
                } catch (e) {
                  console.error('Error parseando apariciones:', e);
                  apariciones = [];
                }
              }

              // Si hay apariciones, usar la primera (de menor posición)
              if (apariciones.length > 0) {
                const sortedApariciones = apariciones.sort((a, b) => {
                  const pageA = parseInt(a.page) || 999;
                  const pageB = parseInt(b.page) || 999;
                  if (pageA !== pageB) return pageA - pageB;
                  const orderA = parseInt(a.orderInPage) || 999;
                  const orderB = parseInt(b.orderInPage) || 999;
                  return orderA - orderB;
                });
                return {
                  pagina: sortedApariciones[0].page || 0,
                  orden_en_pag: sortedApariciones[0].orderInPage || 0
                };
              }

              // Si no hay apariciones, retornar valores vacíos
              return {
                pagina: 0,
                orden_en_pag: 0
              };
            }
            // Para el título, incluir también la URL para el icono de link
            if (column.Field.toLowerCase() === 'titulo' || column.Field.toLowerCase() === 'title') {
              // Buscar el campo de título en los datos reales
              const tituloValue = row[column.OriginalField] || row[column.Field] || row.titulo || row.TITULO || row.title || row.TITLE;
              const urlValue = row.url || row.URL || null;
              return {
                titulo: tituloValue,
                url: urlValue
              };
            }

            
            // Para otras columnas, usar el campo original si existe, sino el field
            const fieldValue = column.OriginalField ? row[column.OriginalField] : row[column.Field];
            return fieldValue;
          },
          header: columnConfigFromResponse?.core?.[column.Field]?.displayName || getColumnDisplayName(column.Field),
          cell: (info: any) => {
            const value = info.getValue();
            const row = info.row;

            // Estilo configurable (font weight, etc.) desde columnConfig.core[<field>].styleId.
            // Otras ramas (Native, mergeWithCatalog) ya lo aplican; las Core simples lo
            // venían ignorando — bug visible en TITULO con styleId='bold'.
            const cellStyleClass = getStylePresetClassName(coreCfg?.styleId);

            // Columna de imagen
            if (columnField === 'imagen' && value) {
              // Obtener información del producto para el modal
              const productTitle = row.original.titulo || row.original.title || 'Producto sin título';
              
              return (
                <div className="flex justify-center items-center">
                  <div 
                    className="relative group w-16 h-14 flex justify-center items-center overflow-hidden rounded-lg border border-gray-200 cursor-pointer"
                    onClick={() => openImageModal(value, productTitle)}
                  >
                    <img
                      src={value}
                      alt="Producto"
                      loading="lazy"
                      className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-110"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zMiAyOEMyOS43OTA5IDI4IDI4IDI5Ljc5MDkgMjggMzJDMjggMzQuMjA5MSAyOS43OTA5IDM2IDMyIDM2QzM0LjIwOTEgMzYgMzYgMzQuMjA5MSAzNiAzMkMzNiAyOS43OTA5IDM0LjIwOTEgMjggMzIgMjhaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0yNCA0MEw0MCA0MEw0MCAyNEwyNCAyNEwyNCA0MFoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+';
                      }}
                    />
                    {/* Overlay de hover */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg transition-all duration-200 flex items-center justify-center">
                      <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            

            
            // Columnas de precio - incluir todos los campos de precio
            const priceFields = [
              'precio', 'precio_desc', 'precio_venta', 'precio_cuota', 
              'original_price', 'discounted_price', 'sale_price', 'installements_value'
            ];
            
            if (priceFields.includes(columnField)) {
              // Manejo especial para precio_cuota cuando no tiene valor
              if ((columnField === 'precio_cuota' || columnField === 'installements_value') && (!value || value === null || value === 'null')) {
                // Log para depurar - eliminar después
                if (row.original.buybox_winner === 1 || row.original.buybox_winner === '1') {
                }
                // Buscar cuotas y precio de venta para calcular
                const cuotasField = ['cuotas', 'installments', 'installements', 'CUOTAS'].find(f => row.original[f]);
                const cuotas = cuotasField ? row.original[cuotasField] : null;
                const salePriceField = ['sale_price', 'precio_venta', 'PRECIO_VENTA'].find(f => row.original[f]);
                const salePrice = salePriceField ? row.original[salePriceField] : null;
                
                if (cuotas && salePrice) {
                  const numCuotas = typeof cuotas === 'string' ? parseInt(cuotas) : cuotas;
                  const numSalePrice = typeof salePrice === 'string' ? parseFloat(salePrice) : salePrice;
                  
                  if (numCuotas > 0 && numSalePrice > 0) {
                    const calculatedValue = numSalePrice / numCuotas;
                    const formattedPrice = formatCurrencyValue(calculatedValue);
                    
                    return (
                      <div className="text-center">
                        <span
                          className="font-bold text-gray-600 bg-gray-50 px-2 py-1 rounded-md whitespace-nowrap inline-block"
                          title="Calculado: Precio de venta / Cuotas"
                        >
                          {formattedPrice}
                        </span>
                      </div>
                    );
                  }
                }
                return <span className="text-gray-400">-</span>;
              }
              
              // Continuar con el manejo normal si tiene valor
              if (value && value !== null && value !== 'null') {
              // Para precio descontado, extraer el valor numérico si es un objeto
              let actualValue = value;
              let discountTooltip = '';
              
              if (value && typeof value === 'object' && value.value) {
                actualValue = value.value;
                discountTooltip = value.discountInfo || '';
              }
              
              const numericValue = parseFloat(actualValue);
              if (!isNaN(numericValue) && numericValue > 0) {
                // Formatear precio como $xxxx.yy
                const formattedPrice = formatCurrencyValue(numericValue);
                
                // Determinar el color según el tipo de precio
                let colorClass = 'text-gray-800';
                let bgClass = 'bg-gray-100';
                
                // Para precio de venta, usar colores según precio de referencia
                if (columnField === 'precio_venta' || columnField === 'sale_price') {
                  const currentPrecioReferencia = precioReferenciaRef.current;
                  if (currentPrecioReferencia && !isNaN(parseFloat(currentPrecioReferencia))) {
                    const refPrice = parseFloat(currentPrecioReferencia);
                    if (numericValue < refPrice) {
                      // Por debajo - Verde
                      colorClass = 'text-green-700';
                      bgClass = 'bg-green-100';
                    } else if (numericValue === refPrice) {
                      // Igual - Celeste
                      colorClass = 'text-cyan-700';
                      bgClass = 'bg-cyan-100';
                    } else {
                      // Por encima - Rojo
                      colorClass = 'text-red-700';
                      bgClass = 'bg-red-100';
                    }
                  } else {
                    // Sin precio de referencia - gris
                    colorClass = 'text-gray-900';
                    bgClass = 'bg-gray-100';
                  }
                } else if (columnField === 'precio_desc' || columnField === 'discounted_price') {
                  colorClass = 'text-gray-700';
                  bgClass = 'bg-gray-200';
                } else if (columnField === 'precio_cuota' || columnField === 'installements_value') {
                  colorClass = 'text-gray-600';
                  bgClass = 'bg-gray-50';
                }
                
                // Calcular tooltip para precio de venta si hay precio de referencia
                let tooltipText = '';
                const currentPrecioReferencia2 = precioReferenciaRef.current;
                if ((columnField === 'precio_venta' || columnField === 'sale_price') && currentPrecioReferencia2 && !isNaN(parseFloat(currentPrecioReferencia2))) {
                  const refPrice = parseFloat(currentPrecioReferencia2);
                  const difference = numericValue - refPrice;
                  const percentageDiff = ((difference / refPrice) * 100);
                  
                  if (difference > 0) {
                    tooltipText = `+${formatCurrencyValue(Math.abs(difference))} (${percentageDiff.toFixed(1)}%)`;
                  } else if (difference < 0) {
                    tooltipText = `-${formatCurrencyValue(Math.abs(difference))} (${percentageDiff.toFixed(1)}%)`;
                  } else {
                    tooltipText = 'Precio igual a referencia';
                  }
                }
                
                // Usar tooltip de descuento si está disponible
                if ((columnField === 'precio_desc' || columnField === 'discounted_price' || columnField.includes('desc')) && discountTooltip) {
                  tooltipText = discountTooltip;
                  
                }

                return (
                  <div className="text-center">
                    <span
                      className={`font-bold ${colorClass} ${bgClass} px-2 py-1 rounded-md whitespace-nowrap inline-block`}
                      title={tooltipText}
                    >
                      {formattedPrice}
                    </span>
                  </div>
                );
              }
            }
            }
            
            // Precio desc - no mostrar null o valores vacíos
            if (columnField === 'precio_desc' && (!value || value === 'null' || value === null || (value && typeof value === 'object' && !value.value))) {
              return <span className="text-gray-400">-</span>;
            }
            
            // Cuotas - mostrar con formato especial
            if (columnField === 'cuotas') {
              // Log para depurar - eliminar después
              if (row.original.buybox_winner === 1 || row.original.buybox_winner === '1') {
              }
              
              if (value) {
                const numericValue = parseInt(value);
                if (!isNaN(numericValue)) {
                  return (
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                      {numericValue}x
                    </span>
                  );
                }
              }
            }
            
            // Valores por defecto
            if (value === null || value === undefined || value === 'null') {
              return <span className="text-gray-400">-</span>;
            }
            
            // Títulos - truncar si son muy largos y agregar icono de link
            if ((columnField === 'titulo' || columnField === 'title') && value) {
              // Función auxiliar para procesar buybox data
              const getBuyboxData = () => {
                const rawBuyboxId = row.original.buybox_id;
                const rawBuyboxWinner = row.original.buybox_winner;
                const rawSeller = row.original.Seller || row.original.seller || '';
                
                // Verificar si buybox_id es realmente NULL o tiene un valor válido
                // En MySQL, NULL se convierte en null en JavaScript
                // "(NULL)" es una cadena de texto, no un valor NULL real
                const isValidBuyboxId = rawBuyboxId !== null && 
                                      rawBuyboxId !== undefined && 
                                      rawBuyboxId !== '' && 
                                      rawBuyboxId !== '(NULL)' && 
                                      rawBuyboxId !== 'NULL' &&
                                      rawBuyboxId !== '0' &&
                                      rawBuyboxId !== 0;
                
                const buyboxId = isValidBuyboxId ? rawBuyboxId : null;
                // Manejar diferentes formatos de buybox_winner incluyendo NULL
              const isBuyboxWinner = rawBuyboxWinner === 1 || 
                                   rawBuyboxWinner === '1' || 
                                   rawBuyboxWinner === true ||
                                   rawBuyboxWinner === 'true' ||
                                   parseInt(rawBuyboxWinner) === 1;
                
                // Validación adicional: solo considerar válidos los IDs numéricos
                if (buyboxId && !/^\d+$/.test(String(buyboxId))) {
                  // Si buyboxId no es un número válido, tratarlo como null
                  return { buyboxId: null, isBuyboxWinner: false, seller: rawSeller };
                }
                
                return { buyboxId, isBuyboxWinner, seller: rawSeller };
              };
              
              getBuyboxData();
              
              // Debug: log solo los primeros productos para ver qué datos estamos recibiendo
              if (!(window as any)._debugShown2) {
                (window as any)._debugShown2 = 0;
              }
              if ((window as any)._debugShown2 < 5) {
                (window as any)._debugShown2++;
              }
              
              // Si value es un objeto (nuestro caso), extraer titulo y url
              if (typeof value === 'object' && value && value.titulo) {
                const titulo = toTitleCase(value.titulo);
                const url = value.url;
                
                return (
                  <div className="flex items-start space-x-2 w-full">
                    <span title={titulo} className={`block flex-1 break-words ${cellStyleClass}`} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {titulo}
                    </span>
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                        title="Ver publicación"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                );
              }
              // Fallback para títulos que son string directo
              else if (typeof value === 'string') {
                const formattedTitle = toTitleCase(value);

                return (
                  <div className="flex items-start space-x-2 w-full">
                    <span title={formattedTitle} className={`block flex-1 break-words ${cellStyleClass}`} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {formattedTitle}
                    </span>
                    {/* Para títulos string, intentar obtener URL desde row original */}
                    {row.original.url && (
                      <a 
                        href={row.original.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                        title="Ver publicación"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                );
              }
            }
            
            // Marca - usar badges como en COMPARAR
            if (columnField === 'marca') {
              // Obtener la fila completa para verificar marca extraída
              const rowData = row.original;
              const extractedBrand = rowData ? extractBrandFromRow(rowData) : null;
              
              // Si hay marca existente, mostrarla con badge azul
              if (value && typeof value === 'string' && value.trim() !== '' && value !== 'null') {
                return (
                  <div className="text-center">
                    <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                      {toTitleCase(value)}
                    </span>
                  </div>
                );
              }
              
              // Si se identificó una marca automáticamente, mostrarla en rojo con badge
              if (extractedBrand) {
                return (
                  <div className="text-center">
                    <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full" title={`Marca identificada automáticamente: ${extractedBrand}`}>
                      {toTitleCase(extractedBrand)}
                    </span>
                  </div>
                );
              }
              
              // Si no hay marca, mostrar guión
              return <span className="text-gray-400">-</span>;
            }
            
            // Categoría - mapear códigos a nombres
            if (columnField === 'categoria' || columnField === 'cat') {
              // Mapear códigos de categoría a nombres - mapeo más completo
              const categoryMap: { [key: string]: string } = {
                // Calzado
                '716': 'Calzado',
                '711': 'Calzado', 
                '717': 'Calzado',
                '7044': 'Calzado',
                '598': 'Calzado',
                // Ropa
                '1': 'Ropa',
                '2': 'Ropa',
                '100': 'Ropa',
                '200': 'Ropa',
                // Accesorios
                '300': 'Accesorios',
                '400': 'Accesorios',
                // Deportes
                '500': 'Deportes',
                '600': 'Deportes',
                // Si ya es texto, mantenerlo
                'calzado': 'Calzado',
                'ropa': 'Ropa',
                'accesorios': 'Accesorios',
                'deportes': 'Deportes'
              };
              
              // Handle both string and object values safely
              let displayValue = '';
              if (value === null || value === undefined) {
                displayValue = '';
              } else if (typeof value === 'string') {
                displayValue = value;
              } else if (typeof value === 'object') {
                // If it's an object, try to extract a meaningful value
                // Check common properties that might contain the category
                displayValue = value.name || value.value || value.Cat || value.cat || value.category || '';
                // If still no value found, convert to string representation
                if (!displayValue && Object.keys(value).length > 0) {
                  // Try to get the first string value from the object
                  const firstStringValue = Object.values(value).find(v => typeof v === 'string') as string | undefined;
                  displayValue = firstStringValue || '';
                }
              } else {
                displayValue = String(value);
              }
              
              const valueStr = displayValue.toLowerCase();
              const categoryName = categoryMap[valueStr] || categoryMap[displayValue] || displayValue;
              
              return (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                  {toTitleCase(categoryName || 'Sin categoría')}
                </span>
              );
            }
            
            // Sitio - mostrar valor exacto de la base de datos
            if (columnField === 'sitio') {
              return (
                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                  {String(value || '')}
                </span>
              );
            }
            
            
            // Columna combinada de página y orden en página - con soporte para múltiples apariciones
            if (columnField === 'pagina_orden') {
              const product = row.original;

              // Verificar si hay apariciones múltiples - buscar en diferentes variaciones del campo
              let apariciones: any[] = [];
              const aparicionesValue = product.apariciones || product.Apariciones;

              if (aparicionesValue) {
                try {
                  apariciones = typeof aparicionesValue === 'string'
                    ? JSON.parse(aparicionesValue)
                    : aparicionesValue;
                  apariciones = Array.isArray(apariciones) ? apariciones : [];
                } catch (e) {
                  console.error('Error parseando apariciones en render:', e);
                  apariciones = [];
                }
              } else {
                // Debug - log solo una vez por tabla
                if (!(window as any)._aparicionesDebugLogged) {
                  (window as any)._aparicionesDebugLogged = true;
                }
              }
              
              const hasMultipleApariciones = apariciones.length > 1;
              
              if (hasMultipleApariciones) {
                return (
                  <div className="text-center relative group">
                    <div className="flex items-center justify-center space-x-1 cursor-help">
                      <List className="h-4 w-4 text-gray-500" />
                      <span className="text-xs text-gray-600">{apariciones.length}</span>
                    </div>
                    {/* Tooltip con todas las apariciones */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 z-50">
                      <div className="bg-gray-800 text-white p-2 rounded-lg shadow-lg min-w-[200px] max-w-[300px]">
                        <div className="text-xs font-semibold mb-1 border-b border-gray-600 pb-1">
                          Múltiples apariciones ({apariciones.length})
                        </div>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {apariciones.map((ap: any, idx: number) => (
                            <div key={idx} className="text-xs text-left">
                              <span className="font-medium">
                                {ap.category_toma || 'Sin categoría'}:
                              </span>{' '}
                              <span className="text-gray-300">
                                Pág. {String(ap.page || 0).padStart(2, '0')}, 
                                Pos. {String(ap.orderInPage || 0).padStart(2, '0')}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              } else if (value && typeof value === 'object') {
                // Mostrar posición normal
                const pagina = parseInt(value.pagina) || 0;
                const orden = parseInt(value.orden_en_pag) || 0;
                
                // Formatear con ceros a la izquierda (001-001)
                const paginaFormatted = pagina.toString().padStart(3, '0');
                const ordenFormatted = orden.toString().padStart(3, '0');
                
                return (
                  <div className="text-center">
                    <span className="text-xs text-gray-600 font-mono">
                      {paginaFormatted}-{ordenFormatted}
                    </span>
                  </div>
                );
              }
              
              // Si no hay datos, mostrar guión
              return (
                <div className="text-center">
                  <span className="text-gray-400">-</span>
                </div>
              );
            }
            
            // Manejo seguro del valor genérico
            if (value === null || value === undefined) {
              return <span className="text-gray-400">-</span>;
            }
            
            // Si es un objeto, intentar extraer un valor significativo
            if (typeof value === 'object') {
              // Intentar propiedades comunes
              const possibleValue = value.value || value.name || value.text || value.label;
              if (possibleValue !== undefined) {
                return String(possibleValue);
              }
              
              // Si es un array, unirlo con comas
              if (Array.isArray(value)) {
                return value.join(', ');
              }
              
              // Como último recurso, intentar obtener el primer valor string del objeto
              const firstStringValue = Object.values(value).find(v => typeof v === 'string' || typeof v === 'number') as string | number | undefined;
              if (firstStringValue !== undefined) {
                return String(firstStringValue);
              }
              
              // Si nada funcionó, mostrar un placeholder
              return <span className="text-gray-400">-</span>;
            }
            
            return String(value);
          },
          enableSorting: columnField !== 'imagen' && columnField !== 'pagina_orden',
          enableColumnFilter: false,
          size: columnField === 'imagen' ? 100 : 
                columnField === 'titulo' ? 380 :
                columnField === 'marca' ? 150 :
                columnField === 'precio' ? 120 :
                columnField === 'precio_desc' ? 120 :
                columnField === 'precio_venta' ? 120 :
                columnField === 'cuotas' ? 80 :
                columnField === 'precio_cuota' ? 120 :
                columnField === 'categoria' ? 100 :
                columnField === 'sitio' ? 120 :
                columnField === 'pagina_orden' ? 100 :
                columnField === 'actions' ? 80 : 120,
        };
      });
      
      
      
      // FORZAR AGREGAR COLUMNA DE ACCIONES SI NO EXISTE
      const hasActionsColumn = tableColumns.some(col => col.id === 'actions');
      
      
      
      
      if (!hasActionsColumn) {
        
        tableColumns.push({
          id: 'actions',
          header: '',
          cell: ({ row }: { row: any }) => {
            const product = row.original;
            
            
            if (comparisonMode) {
              const productId = product.id || product.comparisonId || `${product.titulo}_${product.marca}_${product.sitio}`;
              return (
                <div className="flex justify-center">
                  <button
                    onClick={() => onRemoveFromComparison?.(productId)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remover de comparación"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
                          } else {
                // Verificar si el producto ya está en comparación
                const isInComparison = isProductInComparison(product);
                
                return (
                  <div className="flex justify-center items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onAddToComparison) {
                          try {
                            onAddToComparison(product);
                          } catch (error) {
                            // noop
                          }
                        } else {
                          alert('Función de comparación no disponible');
                        }
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        isInComparison
                          ? 'text-purple-700 hover:text-purple-900 hover:bg-purple-100 bg-purple-200'
                          : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50'
                      }`}
                      title={isInComparison ? "Ya está en comparación" : "Agregar a comparación"}
                    >
                      <GitCompare className={`h-4 w-4 ${isInComparison ? 'text-purple-700' : ''}`} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDataIssuesModalRow(product);
                      }}
                      className="p-2 rounded-lg transition-colors text-gray-400 hover:text-pink-600 hover:bg-pink-50"
                      title="Reportar issue / proponer edición"
                    >
                      <Flag className="h-4 w-4" />
                    </button>
                  </div>
                );
              }
          },
          enableSorting: false,
          enableColumnFilter: false,
          size: 120,
        });
      }
      
      // Agregar columnas de selección y alertas en modo tabla estándar
      if (!comparisonMode) {
        // IMPORTANTE: header y cell leen el estado actual desde table.options.meta
        // en cada render. No capturar `selectedProducts` en el closure de columnas
        // (las columnas solo se reconstruyen al cambiar tabla/comparisonMode/precio).
        const selectionColumn: ColumnDef<TableData> = {
          id: 'selection',
          header: ({ table }: any) => {
            const meta = table.options.meta as any;
            const selected: Set<string> = meta?.selectedProducts ?? new Set();
            const rows = table.getRowModel().rows;
            const currentPageIds: string[] = rows.map((r: any) =>
              r.original.id_catalogo || r.original.ID_CATALOGO || r.original.id
            );
            const allSelected = currentPageIds.length > 0 && currentPageIds.every((id: string) => selected.has(id));
            const someSelected = currentPageIds.some((id: string) => selected.has(id));
            return (
              <div className="flex justify-center">
                <button
                  onClick={() => meta?.toggleSelectAll?.()}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title={allSelected ? t('alerts.deselect_all') : t('alerts.select_all')}
                >
                  {allSelected ? (
                    <CheckSquare className="h-4 w-4 text-pink-500" />
                  ) : someSelected ? (
                    <CheckSquare className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Square className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            );
          },
          cell: ({ row, table }: any) => {
            const productId = row.original.id_catalogo || row.original.ID_CATALOGO || row.original.id;
            const meta = table.options.meta as any;
            const selected: Set<string> = meta?.selectedProducts ?? new Set();
            const isSelected = selected.has(productId);
            return (
              <div className="flex justify-center">
                <button
                  onClick={() => meta?.toggleProductSelection?.(productId, row.index)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  {isSelected ? (
                    <CheckSquare className="h-4 w-4 text-pink-500" />
                  ) : (
                    <Square className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            );
          },
          enableSorting: false,
          enableColumnFilter: false,
          size: 50,
        };

        // TEMPORALMENTE OCULTO - Columna de alertas
        /* const alertColumn: ColumnDef<TableData> = {
          id: 'alert',
          header: () => (
            <div className="flex justify-center">
              <Bell className="h-4 w-4 text-gray-400" />
            </div>
          ),
          cell: ({ row }: { row: any }) => {
            const product = row.original;
            const productId = product.id_catalogo || product.ID_CATALOGO || product.id;
            const title = product.title || product.titulo || product.name || '';
            const sitio = product.sitio || product.site || '';
            const imagen = product.imagen || product.image || '';

            return (
              <div className="flex justify-center">
                <ProductAlertIcon
                  product={{
                    id_catalogo: productId,
                    title,
                    sitio,
                    imagen
                  }}
                  currentTableName={tableName}
                  size="sm"
                />
              </div>
            );
          },
          enableSorting: false,
          enableColumnFilter: false,
          size: 50,
        }; */

        // EXTENDED MODE — columna LUPA (superadmin debug). Render condicional vía meta
        // (extendedMode) para que se prenda/apague sin reconstruir columnas.
        const detailColumn: ColumnDef<TableData> = {
          id: 'extended_detail',
          header: () => (
            <div className="flex justify-center" title="EXTENDED MODE — abrir ficha completa del producto">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
          ),
          cell: ({ row, table }: any) => {
            const meta = table.options.meta as any;
            if (!meta?.extendedMode) return null;
            const productId = row.original.id_catalogo || row.original.ID_CATALOGO || row.original.id;
            if (!productId) return null;
            return (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => meta?.openProductDetail?.(productId)}
                  className="p-1 hover:bg-pink-50 rounded transition-colors"
                  title="Ver ficha completa (EXTENDED MODE)"
                >
                  <Search className="h-4 w-4 text-pink-500" />
                </button>
              </div>
            );
          },
          enableSorting: false,
          enableColumnFilter: false,
          size: 40,
        };

        tableColumns = [selectionColumn, detailColumn, ...tableColumns];
      }

      // Deduplicar columnas por ID para evitar React duplicate key warnings
      {
        const seen = new Set<string>();
        tableColumns = tableColumns.filter(col => {
          const colId = (col as any).id || '';
          if (seen.has(colId)) return false;
          seen.add(colId);
          return true;
        });
      }

      // PT-#67 — preview de template: si viene templateColumnOrder en previewMode,
      // forzamos orden + visibilidad al template (system cols primero) y salimos.
      // No tocamos saved preferences ni el path de producción.
      if (previewMode && Array.isArray(templateColumnOrder) && templateColumnOrder.length > 0) {
        const systemIds = ['selection', 'extended_detail', 'alert'];
        const present = new Set(tableColumns.map(c => c.id!));
        const templateIds = templateColumnOrder.filter(id => present.has(id));
        const templateSet = new Set(templateIds);
        // Orden final: system cols existentes primero, luego el orden del template.
        const systemPresent = systemIds.filter(id => present.has(id));
        const finalOrder = [...systemPresent, ...templateIds];
        // Reordenar defs para que el render inicial ya salga ordenado.
        const reordered = finalOrder
          .map(id => tableColumns.find(col => col.id === id))
          .filter((col): col is ColumnDef<TableData> => col !== undefined)
          .concat(tableColumns.filter(col => !finalOrder.includes(col.id!)));
        setColumns(reordered);
        setColumnOrder(finalOrder);
        // Visibilidad: solo columnas del template (+ system cols + tags) visibles.
        const visibility: Record<string, boolean> = {};
        tableColumns.forEach(col => {
          if (!col.id) return;
          visibility[col.id] = templateSet.has(col.id) || systemIds.includes(col.id);
        });
        systemPresent.forEach(id => { visibility[id] = true; });
        tagColumnsMeta.forEach((column: TagColumnMeta) => {
          if (column.field && templateSet.has(column.field)) visibility[column.field] = true;
        });
        setColumnVisibility(visibility);
        return;
      }

      // Cargar preferencias guardadas
      const savedPreferences = await loadColumnPreferences();

      // TEMPORAL: Limpiar preferencias si no incluyen 'actions' para forzar recreación
      if (savedPreferences && savedPreferences.order && !savedPreferences.order.includes('actions')) {
        // Las preferencias ahora se guardan en Firebase, no es necesario limpiar localStorage
        // Usar configuración por defecto
        const currentOrder = tableColumns.map(col => col.id!);
        // Asegurar que 'selection' y 'extended_detail' estén al inicio
        let finalOrder = currentOrder;
        if (!comparisonMode) {
          if (tableColumns.some(c => c.id === 'extended_detail')) {
            finalOrder = ['extended_detail', ...finalOrder.filter(c => c !== 'extended_detail')];
          }
          if (tableColumns.some(c => c.id === 'selection')) {
            finalOrder = ['selection', ...finalOrder.filter(c => c !== 'selection')];
          }
        }
        setColumnOrder(finalOrder);
        setColumns(tableColumns);
        return;
      }
      
      // Aplicar orden guardado si existe (excepto Kimberly que tiene orden fijo por código)
      if (savedPreferences && savedPreferences.order && savedPreferences.order.length > 0 && !isUnitPriceClient) {
        const orderedColumns = [...tableColumns];
        // Deduplicar savedOrder para evitar columnas duplicadas de preferencias antiguas
        const seenSavedIds = new Set<string>();
        const savedOrder = (savedPreferences.order as string[]).filter((id: string) => {
          if (seenSavedIds.has(id)) return false;
          seenSavedIds.add(id);
          return true;
        });

        // Reordenar columnas según preferencias guardadas
        const reorderedColumns = savedOrder
          .map((columnId: string) => orderedColumns.find(col => col.id === columnId))
          .filter((col): col is ColumnDef<TableData> => col !== undefined) // Filtrar columnas que ya no existen
          .concat(orderedColumns.filter(col => !savedOrder.includes(col.id!))); // Agregar nuevas columnas al final

        setColumns(reorderedColumns);
        const mergedOrder = [
          ...savedOrder,
          ...orderedColumns
            .map(col => col.id!)
            .filter(id => !!id && !savedOrder.includes(id))
        ];
        // Asegurar que 'selection', 'extended_detail' y 'alert' estén al inicio
        let finalMergedOrder = mergedOrder.filter(c => c !== 'selection' && c !== 'alert' && c !== 'extended_detail');
        if (!comparisonMode) {
          if (tableColumns.some(c => c.id === 'alert')) {
            finalMergedOrder = ['alert', ...finalMergedOrder];
          }
          if (tableColumns.some(c => c.id === 'extended_detail')) {
            finalMergedOrder = ['extended_detail', ...finalMergedOrder];
          }
          if (tableColumns.some(c => c.id === 'selection')) {
            finalMergedOrder = ['selection', ...finalMergedOrder];
          }
        }
        setColumnOrder(finalMergedOrder);

        // Aplicar visibilidad guardada si existe
        if (savedPreferences.visibility) {
          const updatedVisibility = { ...savedPreferences.visibility };
          orderedColumns.forEach(col => {
            if (col.id && !(col.id in updatedVisibility)) {
              updatedVisibility[col.id] = true;
            }
          });
          // Forzar visibilidad de columna selection y alert si no es modo comparación
          if (!comparisonMode && tableColumns.some(col => col.id === 'selection')) {
            updatedVisibility.selection = true;
          }
          if (!comparisonMode && tableColumns.some(col => col.id === 'alert')) {
            updatedVisibility.alert = true;
          }
          // EXTENDED MODE — visibilidad de extended_detail siempre sigue al flag global.
          if (tableColumns.some(col => col.id === 'extended_detail')) {
            updatedVisibility.extended_detail = !!extendedMode;
          }
          // Forzar visibilidad de columnas de tags (siempre visibles)
          tagColumnsMeta.forEach((column: TagColumnMeta) => {
            if (column.field) {
              updatedVisibility[column.field] = true;
            }
          });
          setColumnVisibility(updatedVisibility);
        }
      } else {
        // Si no hay preferencias guardadas, inicializar el orden actual
        let currentOrder = tableColumns.map(col => col.id!);

        // Asegurar que 'actions' esté al final si no está presente
        if (!currentOrder.includes('actions')) {
          currentOrder.push('actions');
        }

        // Asegurar que 'selection', 'extended_detail' y 'alert' estén al inicio si no es modo comparación
        currentOrder = currentOrder.filter(c => c !== 'selection' && c !== 'alert' && c !== 'extended_detail');
        if (!comparisonMode) {
          if (tableColumns.some(c => c.id === 'extended_detail')) {
            currentOrder = ['extended_detail', ...currentOrder];
          }
          if (tableColumns.some(c => c.id === 'alert')) {
            currentOrder = ['alert', ...currentOrder];
          }
          if (tableColumns.some(c => c.id === 'selection')) {
            currentOrder = ['selection', ...currentOrder];
          }
        }
        setColumnOrder(currentOrder);
        setColumns(tableColumns);
      }
    } catch (err: any) {
      console.error('Error loading table schema:', {
        tableName,
        error: err.message,
        responseData: err.response?.data,
        timestamp: new Date().toISOString()
      });
      
      // Crear un mensaje de error más específico
      let errorMessage = 'Error al cargar el esquema de la tabla';
      
      if (err.response) {
        // El servidor respondió con un error
        const status = err.response.status;
        const serverMessage = err.response.data?.error || err.response.data?.details || 'Error del servidor';
        
        if (status === 404) {
          errorMessage = `La tabla "${tableName}" no existe o no está disponible`;
        } else if (status === 500) {
          errorMessage = `Error del servidor: ${serverMessage}`;
        } else {
          errorMessage = `Error ${status}: ${serverMessage}`;
        }
      } else if (err.request) {
        // La petición se hizo pero no se recibió respuesta
        errorMessage = 'No se pudo conectar con el servidor. Verifique su conexión a internet.';
      } else {
        // Error en la configuración de la petición
        errorMessage = `Error de configuración: ${err.message}`;
      }
      

      
      setError(errorMessage);
    } finally {
      // Cierre defensivo: si abrió setupColumns, lo finaliza acá. Si rompió antes
      // de abrirlo (ej. error en fetch del schema), cerramos schema con errorFlag.
      if (finishSetupColumns) {
        try { finishSetupColumns({ done: true }); } catch { /* noop */ }
      } else {
        try { finishSchema({ aborted: true }); } catch { /* noop */ }
      }
      setLoading(false);
    }
  };

  // Función para formatear fecha internacionalizada
  const formatDate = (date: Date): string => {
    const monthKeys = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    
    const day = date.getDate();
    const monthKey = monthKeys[date.getMonth()];
    const month = t(`months.${monthKey}`);
    const year = date.getFullYear();
    const preposition = t('date.date_preposition');
    const dataFrom = t('date.data_from');
    

    
    return `${dataFrom} ${day} ${preposition} ${month}, ${year}`;
  };

  // Función para obtener nombres de columnas más amigables y normalizados
  const getColumnDisplayName = (fieldName: string): string => {
    const fieldLower = fieldName.toLowerCase();
    
    // Mapeo de campos a claves de traducción
    const translationKeys: { [key: string]: string } = {
      'imagen': 'imagen',
      'marca': 'marca',
      'titulo': 'titulo',
      'title': 'titulo',
      'buybox': 'buybox',
      'seller': 'seller',
      'precio': 'precio',
      'precio_desc': 'precio_desc',
      'precio_venta': 'precio_venta',
      'cuotas': 'cuotas',
      'precio_cuota': 'precio_cuota',
      'categoria': 'categoria',
      'sitio': 'sitio',
      'pagina_orden': 'pagina_orden',
      // Nuevos nombres de columnas
      'original_price': 'precio',
      'discounted_price': 'precio_desc',
      'sale_price': 'precio_venta',
      'cat': 'categoria',
      'installements': 'cuotas',
      'installements_value': 'precio_cuota',
      'precio_unidad': 'precio_unidad',
      'precio_unidad_medida': 'precio_unidad_medida',
      'contenido_total': 'contenido_total',
      'udm': 'udm'
    };
    
    // Si tenemos una traducción, usarla
    const translationKey = translationKeys[fieldLower];
    if (translationKey) {
      return t(`columns.${translationKey}`);
    }
    
    // Tag columns: "tag_marca" → "Marca (tag)"
    if (fieldLower.startsWith('tag_')) {
      const tagName = fieldName.substring(4) // Quitar "tag_"
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      return `${tagName} (tag)`;
    }

    // Normalizar automáticamente: Primera letra mayúscula, resto minúscula
    return fieldName
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Cargar opciones para los filtros dropdown
  // Función loadFilterOptions eliminada - los filtros se manejan en UnifiedFilterPanel

  // Función para identificar marca desde URL o título
  const identifyBrandFromText = (text: string): string | null => {
    if (!text || typeof text !== 'string') return null;
    
    const textLower = text.toLowerCase();
    
    // Si es una URL, extraer marcas de los segmentos de la URL
    if (textLower.includes('http') || textLower.includes('www.') || textLower.includes('.com')) {
      const brandFromUrl = extractBrandFromUrl(textLower);
      if (brandFromUrl) return brandFromUrl;
    }
    
    // Buscar marca en el texto completo
    for (const brand of BRAND_REFERENCE_LIST) {
      // Buscar la marca como palabra completa (evitar falsos positivos)
      const brandRegex = new RegExp(`\\b${brand.replace(/\s+/g, '\\s*')}\\b`, 'i');
      if (brandRegex.test(textLower)) {
        return brand;
      }
    }
    
    return null;
  };

  // Función específica para extraer marca de URLs
  const extractBrandFromUrl = (url: string): string | null => {
    try {
      // 
      
      // Remover protocolo y www
      let cleanUrl = url.replace(/https?:\/\//, '').replace(/www\./, '');
      // 
      
      // Dividir por '/' para obtener segmentos
      const segments = cleanUrl.split('/').filter(segment => segment.length > 0);
      // 
      
      // Buscar marca en cada segmento
      for (const segment of segments) {
        // 
        
        // Dividir por guiones para obtener palabras individuales
        const words = segment.split('-').filter(word => word.length > 0);
        // 
        
        for (const word of words) {
          // Verificar si alguna palabra coincide con nuestras marcas
          for (const brand of BRAND_REFERENCE_LIST) {
            const normalizedBrand = brand.toLowerCase().replace(/[\s']/g, '');
            if (word === normalizedBrand) {
              // 
              // Retornar la marca formateada correctamente
              if (word === 'oneill') return "O'Neill";
              return brand;
            }
          }
        }
        
        // También buscar el segmento completo, pero con word-boundary
        // por guiones para evitar substring traps (ej: "ver" dentro de
        // "in-ver-ter" no debe matchear como marca "Ver").
        // URLs MercadoLibre usan guiones como separador de palabras.
        const segmentWords = segment.split('-').filter(w => w.length > 0);
        for (const brand of BRAND_REFERENCE_LIST) {
          const normalizedBrand = brand.toLowerCase().replace(/[\s']/g, '');
          // Match exacto contra alguna palabra del segmento (boundary por guion)
          if (segmentWords.includes(normalizedBrand)) {
            //
            if (segmentWords.includes('oneill')) return "O'Neill";
            return brand;
          }
        }
      }
      
      // Buscar patrones especiales antes de números al final
      // Ejemplo: campera-zone-oneill-693479
      const lastSegment = segments[segments.length - 1];
      if (lastSegment) {
        // 
        
        // Buscar patrón: marca-números al final
        const match = lastSegment.match(/([a-zA-Z]+)-(\d+)$/);
        if (match) {
          const possibleBrand = match[1].toLowerCase();
          // 
          
          for (const brand of BRAND_REFERENCE_LIST) {
            const normalizedBrand = brand.toLowerCase().replace(/[\s']/g, '');
            if (possibleBrand === normalizedBrand) {
              // 
              if (possibleBrand === 'oneill') return "O'Neill";
              return brand;
            }
          }
        }
        
        // Buscar en los últimos segmentos antes de números
        const words = lastSegment.split('-');
        // 
        
        for (let i = words.length - 1; i >= 0; i--) {
          const word = words[i];
          // Si es un número, continúa
          if (/^\d+$/.test(word)) {
            // 
            continue;
          }
          
          // 
          
          // Verificar si es una marca
          for (const brand of BRAND_REFERENCE_LIST) {
            const normalizedBrand = brand.toLowerCase().replace(/[\s']/g, '');
            if (word === normalizedBrand) {
              // 
              if (word === 'oneill') return "O'Neill";
              return brand;
            }
          }
          
          // Solo revisar las últimas 3 palabras antes de números
          if (words.length - i > 3) break;
        }
      }
      
      // 
      
    } catch (error) {
      
    }
    
    return null;
  };

  // Función para extraer marca de una fila de datos
  const extractBrandFromRow = (row: TableData): string | null => {
    if (!brandIdentificationEnabled) return null;
    
    // Si ya tiene marca definida, no extraer
    const existingBrand = row['Marca'] || row['marca'] || row['MARCA'];
    if (existingBrand && existingBrand.toString().trim() !== '' && existingBrand !== 'null') {
      return null;
    }
    
    // Intentar extraer de la URL
    const url = row['url'] || row['URL'];
    if (url) {
      const brandFromUrl = identifyBrandFromText(url.toString());
      if (brandFromUrl) return brandFromUrl;
    }
    
    // Intentar extraer del título
    const titulo = row['titulo'] || row['title'];
    if (titulo) {
      let titleText = '';
      if (typeof titulo === 'object' && titulo.titulo) {
        titleText = titulo.titulo;
      } else if (typeof titulo === 'string') {
        titleText = titulo;
      }
      
      const brandFromTitle = identifyBrandFromText(titleText);
      if (brandFromTitle) return brandFromTitle;
    }
    
    return null;
  };

  // Cargar datos de la tabla
  const loadTableData = async () => {
    // Evitar cargar datos en modo comparación
    if (comparisonMode) {
      return;
    }

    // Preview mode: usar sample local sin fetch.
    if (previewMode) {
      const sample = Array.isArray(previewSampleRows) ? previewSampleRows.slice(0, 100) : [];
      setData(sample);
      hadDataRef.current = sample.length > 0;
      setTotalRows(sample.length);
      setTotalUnfilteredRows(sample.length);
      setTotalPages(1);
      setLoading(false);
      return;
    }

    setLoading(true);
    const finishLoadData = debugStep('loadTableData', {
      tableName,
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
    });
    let dataRowCount = 0;
    try {
      // Obtener el nombre real de la columna para el ordenamiento
      let orderBy = null;
      let orderDirection = 'ASC';

      if (sorting.length > 0) {
                 // Mapear nombres de columnas a sus nombres reales
         const columnMapping: { [key: string]: string } = {
           'precio': 'original_price',
           'precio_desc': 'discounted_price', 
           'precio_venta': 'sale_price',
           'cuotas': 'installements',
           'precio_cuota': 'installements_value',
           'marca': 'Marca',
           'titulo': 'title',
           'categoria': 'Cat'
         };
         
                  orderBy = columnMapping[sorting[0].id] || sorting[0].id;
         orderDirection = sorting[0].desc ? 'DESC' : 'ASC';
      }
      
      // Usar filtros externos si hideFilters es true, sino construir filtros internos
      let filters: { [key: string]: string } = {};
      
      if (hideFilters && externalFilters) {
        // Usar filtros externos
        filters = { ...externalFilters };
      } else {
        // Este bloque ya no se ejecuta - siempre usamos hideFilters=true
        // Los filtros se manejan en UnifiedFilterPanel
      }

      // Preparar parámetros, convirtiendo objetos a strings
      const paramObject: { [key: string]: string } = {
        limit: pagination.pageSize.toString(),
        offset: (pagination.pageIndex * pagination.pageSize).toString(),
        ...(orderBy && { orderBy, orderDirection })
      };
      
      // Procesar filtros, stringificando objetos si es necesario
      Object.entries(filters).forEach(([key, value]) => {
        if (typeof value === 'object') {
          paramObject[key] = JSON.stringify(value);
        } else {
          paramObject[key] = String(value);
        }
      });

      const params = new URLSearchParams(paramObject);

      const response = await api.get(`/api/tables/${tableName}/data?${params}`);
      const result: TableResponse = response.data;

      // T-1 (rediseño): las tomas no visibles ya no llegan a la lista (se filtran en
      // /tomas-con-fechas) y el backend responde 403 en el gate defensivo. No hay
      // estado "procesando" en la UI.

      // Log específico para campos de seller y buybox
      if (result.data.length > 0) {
        const sampleProduct = result.data[0];
        
        // Verificar si los sitios están en Seller
        if (sampleProduct.Seller && (sampleProduct.Seller.includes('.') || sampleProduct.Seller.includes('MERCADO'))) {
        }
      }
      
      setData(result.data);
      dataRowCount = result.data.length;
      if (result.data.length > 0) hadDataRef.current = true;
      setTotalRows(result.total);
      setTotalUnfilteredRows(result.totalUnfiltered);
      setTotalPages(result.pages || result.totalPages || Math.ceil(result.total / pagination.pageSize));
    } catch (err: any) {
      console.error('Error loading table schema:', {
        tableName,
        error: err.message,
        responseData: err.response?.data,
        timestamp: new Date().toISOString()
      });
      setError('Error al cargar los datos de la tabla');

    } finally {
      try { finishLoadData({ rowCount: dataRowCount }); } catch { /* noop */ }
      setLoading(false);
    }
  };

  // PT-#67: keys estables para el dep array del effect de schema. En previewMode
  // el reorder del template depende de estos props; en producción son
  // undefined/estables → no afectan el fetch normal.
  const previewTemplateKey = previewMode ? JSON.stringify(templateColumnOrder) : null;
  const previewRowsKey = previewMode ? String(previewSampleRows?.length ?? 0) : null;

  useEffect(() => {

    // No cargar datos si estamos en transición
    if (isTableTransitioning) {
      return;
    }

    const loadData = async () => {
      if (!comparisonMode && tableName) {
        await loadTableSchema();
        // Los filtros se manejan en UnifiedFilterPanel
      }
    };

    loadData();
    // BUG-FIX (2026-06-05): removido `externalFilters` de deps. El schema NO
    // cambia al aplicar filtros (sitio=Carrefour). Antes: cambiar filtro →
    // schema re-fetch → recrea columns → loadTableData se re-dispara → 2 calls
    // a /data. Schema sólo depende de tableName/comparisonMode/lang.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName, comparisonMode, isTableTransitioning, i18n.language, bulkRefreshNonce,
    previewTemplateKey, previewRowsKey]);

  // Update ref and recreate columns when reference price changes
  useEffect(() => {
    precioReferenciaRef.current = precioReferencia;
    if (columns.length > 0 && !comparisonMode) {
      loadTableSchema();
    }
  }, [precioReferencia]);

  // useEffect para crear columnas de comparación
  useEffect(() => {
    if (comparisonMode && comparisonData.length > 0) {
      // Siempre recrear columnas cuando entramos en modo comparación
      createComparisonColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonMode, comparisonData.length]);

  // useEffect separado para datos de comparación - SOLO cuando cambia comparisonData
  useEffect(() => {
    if (comparisonMode) {

      setData(comparisonData);
      setTotalRows(comparisonData.length);
      setTotalUnfilteredRows(comparisonData.length);
      setTotalPages(Math.ceil(comparisonData.length / pagination.pageSize));
      setLoading(false); // Asegurar que no quede en loading
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonData]); // SOLO comparisonData, no pagination.pageSize

  // useEffect para cargar SKUs cuando los datos cambian
  useEffect(() => {
    if (enableSku && data.length > 0) {
      const catalogIds = data
        .map(row => row.id_catalogo || row.ID_CATALOGO || row.id)
        .filter(id => id != null)
        .map(id => String(id));

      if (catalogIds.length > 0) {
        loadSkus(catalogIds);
      }
    }
  }, [data, enableSku, loadSkus]);

  useEffect(() => {
    
    if (columns.length > 0 && !comparisonMode) {
      loadTableData();
    } else {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, hideFilters 
    ? [tableName, columns, sorting, pagination, externalFilters] // Solo filtros externos cuando hideFilters=true
    : [tableName, columns, sorting, columnFilters, globalFilter, pagination, externalFilters]); // Variables de filtros locales eliminadas

  // Click outside handlers eliminados - los dropdowns se manejan en UnifiedFilterPanel

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter,
      pagination,
    },
    manualPagination: true,
    pageCount: totalPages,
    onPaginationChange: setPagination,
    // Meta: expone estado/handlers fresh a los renderers de columnas que se
    // construyen una sola vez en loadTableSchema. Los cells leen via
    // table.options.meta para evitar closures stale (fix bug selección 2026-05-11).
    meta: {
      selectedProducts,
      toggleProductSelection,
      toggleSelectAll,
      extendedMode,
      openProductDetail: (id: string) => setSelectedDetailId(id),
    },
  });

  // Mantener ref al table actualizado (lo usa el listener de teclado para leer
  // rows visibles sin recapturar closures).
  tableRef.current = table;

  // ---- Keyboard navigation: activar foco inicial cuando BulkActionBar aparece
  // y resetear cuando se vacía la selección.
  const bulkBarVisible = !comparisonMode && extendedMode && selectedIds.length >= 1;
  useEffect(() => {
    if (bulkBarVisible) {
      setFocusedRowIndex((current) => {
        if (current !== null) return current;
        const rowsLen = tableRef.current?.getRowModel()?.rows?.length ?? 0;
        if (rowsLen === 0) return null;
        const last = lastSelectedRowIndexRef.current;
        if (typeof last === 'number' && last >= 0 && last < rowsLen) return last;
        return 0;
      });
    } else {
      // BulkActionBar oculta → desactivar foco.
      setFocusedRowIndex(null);
    }
  }, [bulkBarVisible]);

  // ---- Keyboard listener: ↑/↓ navegación, espacio toggle, Esc clear.
  // Activo SOLO cuando bulkBarVisible. Si no, cleanup desmonta el listener.
  useEffect(() => {
    if (!bulkBarVisible) return;

    const isTypingTarget = (el: Element | null) => {
      if (!el) return false;
      const tag = (el.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if ((el as HTMLElement).isContentEditable) return true;
      return false;
    };

    const isModalOpen = () => {
      // Heurística defensiva: cualquier modal/dialog activo en el árbol → no
      // interceptar teclas. Cubre BulkConfirmModal, BulkEditModal,
      // DataIssuesModal, ProductDetailModal, etc.
      // 1) ARIA estándar.
      if (document.querySelector('[role="dialog"], [aria-modal="true"]')) return true;
      // 2) Patrón de modal en este repo: overlay full-screen con "fixed inset-0".
      // Buscamos elementos con esas dos clases simultáneas — match razonablemente
      // específico (los modales del proyecto usan z-50 + bg-black/40 etc).
      const overlays = document.querySelectorAll('div.fixed.inset-0');
      for (let i = 0; i < overlays.length; i++) {
        const el = overlays[i] as HTMLElement;
        // Excluir overlays decorativos (sin contenido interactivo). Si tiene al
        // menos un botón o input dentro, lo consideramos modal activo.
        if (el.querySelector('button, input, textarea, select, [contenteditable="true"]')) {
          return true;
        }
      }
      return false;
    };

    const handler = (e: KeyboardEvent) => {
      if (isTypingTarget(document.activeElement)) return;
      if (isModalOpen()) return;

      const t = tableRef.current;
      if (!t) return;
      const rows = t.getRowModel().rows as any[];
      if (!rows || rows.length === 0) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setFocusedRowIndex((i) => {
            if (i === null) return 0;
            return (i + 1) % rows.length;
          });
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setFocusedRowIndex((i) => {
            if (i === null) return rows.length - 1;
            return (i - 1 + rows.length) % rows.length;
          });
          break;
        }
        case ' ':
        case 'Spacebar': {
          e.preventDefault();
          setFocusedRowIndex((i) => {
            if (i === null) return i;
            const row = rows[i];
            if (!row) return i;
            const productId = row.original.id_catalogo || row.original.ID_CATALOGO || row.original.id;
            if (productId !== undefined && productId !== null) {
              toggleProductSelection(String(productId), i);
            }
            return i;
          });
          break;
        }
        case 'Escape': {
          e.preventDefault();
          clearSelection();
          setFocusedRowIndex(null);
          lastSelectedRowIndexRef.current = null;
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [bulkBarVisible, toggleProductSelection, clearSelection]);

  // ---- Auto-scroll a la row con foco si está fuera del viewport.
  useEffect(() => {
    if (focusedRowIndex === null) return;
    const el = rowRefsMap.current.get(focusedRowIndex);
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch {
      // scrollIntoView opciones no soportadas → fallback silencioso.
      el.scrollIntoView();
    }
  }, [focusedRowIndex]);


  // Función auxiliar para cargar variantes de buybox
  const loadBuyboxVariantsForProducts = async (products: any[], updateProgress?: (msg: string) => void) => {
    const productsWithVariants: any[] = [];
    let _variantsLoaded = 0;
    let _productsWithBuyboxCount = 0;
    const MAX_CONCURRENT_REQUESTS = 5; // Limitar las peticiones concurrentes
    
    // Procesar productos en lotes para evitar sobrecarga
    const processBatch = async (batch: any[]) => {
      const promises = batch.map(async (product) => {
        const result = {
          product,
          variants: [] as any[]
        };
        
        // Si tiene buybox_id, cargar sus variantes
        const buyboxId = product.buybox_id || product.BUYBOX_ID || product.BuyBox_ID;
        const buyboxWinner = product.buybox_winner || product.BUYBOX_WINNER;
        const productId = product.id_catalogo || product.ID_CATALOGO || product.id;
        
        const shouldLoadVariants = (buyboxId && buyboxId !== '(NULL)' && buyboxId !== 'NULL') ||
                                  (buyboxWinner && (buyboxWinner === 1 || buyboxWinner === '1' || buyboxWinner === true));
        
        if (shouldLoadVariants && buyboxId && buyboxId !== '(NULL)' && buyboxId !== 'NULL') {
          _productsWithBuyboxCount++;
          try {
            const response = await api.get(`/api/tables/${tableName}/buybox/${buyboxId}`);
            const variants = response.data.variants || [];

            result.variants = variants.map((variant: any) => ({
              ...variant,
              is_buybox_variant: true,
              parent_id_catalogo: productId
            }));
            _variantsLoaded += variants.length;
          } catch (error) {
            // Error silencioso al cargar variantes
          }
        }

        return result;
      });

      return Promise.all(promises);
    };

    // Procesar productos en lotes
    for (let i = 0; i < products.length; i += MAX_CONCURRENT_REQUESTS) {
      const batch = products.slice(i, i + MAX_CONCURRENT_REQUESTS);
      const results = await processBatch(batch);
      
      // Agregar productos y sus variantes al resultado
      results.forEach(({ product, variants }) => {
        productsWithVariants.push(product);
        productsWithVariants.push(...variants);
      });
      
      // Actualizar progreso si es necesario
      if (updateProgress && i % 20 === 0 && products.length > 100) {
        const progress = Math.round((i / products.length) * 100);
        updateProgress(`Cargando variantes de BuyBox... ${progress}%`);
      }
    }
    
    return productsWithVariants;
  };
  
  // Función para manejar la exportación
  const handleExport = async (filename: string, includeBuyboxVariants?: boolean) => {
    // PT-#67 override (b): el EXPORT de PUBS SIEMPRE usa el template `publicaciones`
    // (orden + on/off), independiente del reorder que el usuario haya hecho en la
    // grilla en vivo. Sin template → fallback al comportamiento actual (todas las
    // leaf columns en orden de definición). Las columnas de sistema (selection,
    // extended_detail, alert, actions) y las que no están en el template se
    // appendean al final (exportToExcel ya ignora las de control).
    const applyPublicacionesTemplate = (
      cols: ReturnType<typeof table.getAllLeafColumns>
    ) => {
      // El modo comparación arma columnas propias (createComparisonColumns) cuyos
      // ids no matchean el id-space del template → no aplicar ahí.
      if (comparisonMode) return cols;
      const tpl = columnConfigSnapshot?.templates?.publicaciones;
      if (!tpl || !Array.isArray(tpl.items) || tpl.items.length === 0) return cols;
      const byId = new Map(cols.map((c) => [c.id, c]));
      const leafIds = cols.map((c) => c.id);
      // resolveTemplateOrder: items enabled+presentes ordenados por el template;
      // descarta los enabled=false; appendea los ids no presentes en el template
      // (tags/sku nuevos + columnas de sistema) al final.
      const orderedIds = resolveTemplateOrder(leafIds, tpl);
      const ordered = orderedIds
        .map((id) => byId.get(id))
        .filter((c): c is (typeof cols)[number] => !!c);
      return ordered.length > 0 ? ordered : cols;
    };
    const visibleColumns = applyPublicacionesTemplate(table.getAllLeafColumns());

    // Si es modo comparación, usar los datos de comparación
    if (comparisonMode && comparisonData) {
      let dataToExport = comparisonData;
      
      if (includeBuyboxVariants) {
        setShowExportProgress(true);
        setExportProgress(20);
        setExportMessage('Cargando variantes de BuyBox...');
        dataToExport = await loadBuyboxVariantsForProducts(comparisonData, setExportMessage);
        setExportProgress(80);
        setExportMessage(`Variantes cargadas. Total de registros: ${dataToExport.length.toLocaleString()}`);
        
        // Esperar un momento antes de exportar
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setExportProgress(95);
      setExportMessage('Generando archivo Excel...');
      
      exportToExcel({
        columns: visibleColumns,
        data: dataToExport,
        filename,
        includeBuyboxVariants,
        skusMap: skusRef.current,
        columnConfig: columnConfigSnapshot
      });

      if (includeBuyboxVariants) {
        setExportProgress(100);
        setExportMessage('¡Exportación completada con variantes de BuyBox!');
        setExportComplete(true);
        
        // Cerrar el progreso después de un momento
        setTimeout(() => {
          setShowExportProgress(false);
        }, 2000);
      }
      
      return;
    }
    
    // Para modo normal, descargar en chunks
    try {
      // Inicializar progress toast
      setExportProgress(0);
      setExportComplete(false);
      setShowExportProgress(true);
      setExportMessage('Calculando total de registros...');
      
      // Primero obtener el total de registros
      const countParams = new URLSearchParams();
      countParams.append('limit', '1');
      countParams.append('offset', '0');

      // BUG-005 FIX (2026-06-05): unificar serialización de filtros con
      // loadTableData. La lógica anterior:
      //   - Arrays → `.join('|')`, pero el backend parseaba algunos filtros array
      //     con JSON.parse → fallaba el parse y NO se aplicaba el filtro.
      //   - Objetos no-array (search_operators) → `String(value)` = '[object Object]'
      //     en vez de JSON.stringify → backend ignora.
      // loadTableData (que SÍ muestra los counts correctos en el grid) usa
      // `typeof value === 'object' ? JSON.stringify : String`. Replicamos eso.
      const appendFilters = (target: URLSearchParams) => {
        if (!externalFilters) return;
        Object.entries(externalFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return;
          if (typeof value === 'object') {
            target.append(key, JSON.stringify(value));
          } else {
            target.append(key, String(value));
          }
        });
      };
      appendFilters(countParams);

      const countResponse = await api.get(`/api/tables/${tableName}/data?${countParams}`);
      const totalRecords = countResponse.data.total || 0;

      // BUG-005 FIX (2026-06-05): defensive check — si el grid muestra `totalRows`
      // (filtered) y el countResponse devuelve algo wildly diferente (>10% diff),
      // loguear warning. Sirve para detectar regresiones del filter sync futuro.
      if (totalRows > 0 && totalRecords > 0 && Math.abs(totalRecords - totalRows) / totalRows > 0.1) {
        console.warn('[export] count mismatch entre grid y export', {
          grid_total: totalRows,
          export_count: totalRecords,
          filters: externalFilters,
          tableName,
        });
      }

      setExportMessage(`Preparando exportación de ${totalRecords.toLocaleString()} registros...`);
      setExportProgress(5);
      
      // Si hay menos de 50k registros, descargar todo de una vez
      if (totalRecords <= 50000) {
        const params = new URLSearchParams(countParams);
        params.set('limit', totalRecords.toString());
        
        setExportProgress(20);
        setExportMessage(`Descargando ${totalRecords.toLocaleString('es-AR')} productos del servidor...`);
        
        const response = await api.get(`/api/tables/${tableName}/data?${params}`, {
          onDownloadProgress: (progressEvent: any) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 60) / progressEvent.total) + 20;
              setExportProgress(percentCompleted);
            }
          }
        });
        
        if (response.data && response.data.data) {
          let dataToExport = response.data.data;
          
          if (includeBuyboxVariants) {
            setExportProgress(70);
            setExportMessage('Cargando variantes de BuyBox...');
            dataToExport = await loadBuyboxVariantsForProducts(response.data.data, setExportMessage);
            setExportMessage(`Variantes cargadas. Total de registros: ${dataToExport.length.toLocaleString()}`);
          }
          
          setExportProgress(85);
          setExportMessage(`Procesando ${dataToExport.length.toLocaleString()} registros...`);
          
          await new Promise(resolve => setTimeout(resolve, 300));
          
          exportToExcel({
            columns: visibleColumns,
            data: dataToExport,
            filename,
            includeBuyboxVariants,
            skusMap: skusRef.current,
            columnConfig: columnConfigSnapshot
          });

          setExportProgress(100);
          setExportMessage(`¡Exportación completada! ${dataToExport.length.toLocaleString()} registros`);
          setExportComplete(true);
        }
      } else {
        // Para más de 50k registros, descargar en chunks
        const CHUNK_SIZE = 25000;
        const chunks = Math.ceil(totalRecords / CHUNK_SIZE);
        let allData: any[] = [];
        
        setExportMessage(`Descargando ${totalRecords.toLocaleString()} registros en ${chunks} partes...`);
        
        for (let i = 0; i < chunks; i++) {
          const chunkParams = new URLSearchParams(countParams);
          chunkParams.set('limit', CHUNK_SIZE.toString());
          chunkParams.set('offset', (i * CHUNK_SIZE).toString());
          
          const progressBase = 10 + (i * 70 / chunks);
          const progressEnd = 10 + ((i + 1) * 70 / chunks);
          
          setExportMessage(`Descargando parte ${i + 1} de ${chunks}...`);
          setExportProgress(progressBase);
          
          try {
            const response = await api.get(`/api/tables/${tableName}/data?${chunkParams}`, {
              onDownloadProgress: (progressEvent: any) => {
                if (progressEvent.total) {
                  const chunkProgress = (progressEvent.loaded / progressEvent.total);
                  const totalProgress = progressBase + (chunkProgress * (progressEnd - progressBase));
                  setExportProgress(Math.round(totalProgress));
                }
              }
            });
            
            if (response.data && response.data.data) {
              allData = allData.concat(response.data.data);
            }
          } catch (chunkError) {
            setExportMessage(`Error descargando parte ${i + 1}. Continuando...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        
        if (allData.length > 0) {
          let dataToExport = allData;
          
          if (includeBuyboxVariants) {
            setExportProgress(80);
            setExportMessage('Cargando variantes de BuyBox...');
            dataToExport = await loadBuyboxVariantsForProducts(allData, setExportMessage);
            setExportMessage(`Variantes cargadas. Total de registros: ${dataToExport.length.toLocaleString()}`);
          }
          
          setExportProgress(85);
          setExportMessage(`Procesando ${dataToExport.length.toLocaleString()} registros...`);
          
          await new Promise(resolve => setTimeout(resolve, 500));
          
          exportToExcel({
            columns: visibleColumns,
            data: dataToExport,
            filename,
            includeBuyboxVariants,
            skusMap: skusRef.current,
            columnConfig: columnConfigSnapshot
          });

          setExportProgress(100);
          setExportMessage(`¡Exportación completada! ${dataToExport.length.toLocaleString()} registros`);
          setExportComplete(true);
        } else {
          throw new Error('No se pudieron descargar los datos');
        }
      }
    } catch (error) {
      setExportMessage('Error en la exportación. Intentando con datos actuales...');
      setExportProgress(0);
      
      // Esperar un momento antes de cerrar
      await new Promise(resolve => setTimeout(resolve, 2000));
      setShowExportProgress(false);
      
      // En caso de error, exportar solo los datos actuales
      if (data.length > 0) {
        let dataToExport = data;
        
        if (includeBuyboxVariants) {
          dataToExport = await loadBuyboxVariantsForProducts(data, () => {});
        }
        
        exportToExcel({
          columns: visibleColumns,
          data: dataToExport,
          filename,
          includeBuyboxVariants,
          skusMap: skusRef.current,
          columnConfig: columnConfigSnapshot
        });
      }
    }
  };

  if (error) {

    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-red-500 text-lg font-semibold mb-2">Error</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <button
            onClick={async () => {
              setError(null);
              
              try {
                await loadTableSchema();
                // Los filtros se manejan en UnifiedFilterPanel
              } catch (retryError) {
                
              }
            }}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
            <span>Reintentar</span>
          </button>
        </div>
      </div>
    );
  }

  
  return (
    <div className="w-full space-y-4">
      {/* Encabezado principal */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center space-x-2">
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Botón de filtros eliminado - se usa UnifiedFilterPanel */}
        </div>
      </div>

      {/* La UI de filtros se maneja en UnifiedFilterPanel */}

      {/* Sección de filtros eliminada - se usa UnifiedFilterPanel */}

      {/* Fila de Precio de Referencia + switch IA (superadmin) + controles de vista.
          El contador "mostrando X-Y de Z" + paginación viven ahora en <TableNavigation />,
          renderizado justo debajo (arriba de la tabla) y al final (abajo de la tabla). */}
      {(loading && !hadDataRef.current) || (!loading && data.length === 0) ? null : <div className="flex items-center justify-between">
        <div className="text-xs text-gray-600 px-3 tabular-nums">
          {totalUnfilteredRows > totalRows && (
            <span className="text-blue-600 font-medium">
              Filtro: {totalRows.toLocaleString('es-AR')} de {totalUnfilteredRows.toLocaleString('es-AR')} Productos
            </span>
          )}
        </div>

        {/* Campo de precio de referencia y controles de vista */}
        <div className="flex items-center space-x-4">
          {/* Precio de referencia */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('table.reference_price')}:</label>
              <div className="flex items-center space-x-1 relative">
                <span className="text-sm text-gray-500">$</span>
                <input
                  type="number"
                  value={precioReferencia}
                  onChange={(e) => setPrecioReferencia(e.target.value)}
                  placeholder="0.00"
                  className="w-32 px-3 py-1 pr-8 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {precioReferencia && (
                  <button
                    onClick={() => setPrecioReferencia('')}
                    className="absolute right-2 text-gray-400 hover:text-gray-600 text-sm font-bold"
                    title={t('table.clear_reference_price')}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            
            {precioReferencia && (
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>{t('table.lower')}</span>
                <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                <span>{t('table.equal')}</span>
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span>{t('table.higher')}</span>
              </div>
            )}
          </div>

          {/* Controles de vista */}
          {onViewModeChange && (
            <div className="flex items-center space-x-4">
              {/* Selector de vista */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => onViewModeChange('table')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'table'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  title={t('view.switch_to_table')}
                >
                  <List className="h-4 w-4" />
                  <span>{t('view.table')}</span>
                </button>
                <button
                  onClick={() => onViewModeChange('grid')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  title={t('view.switch_to_grid')}
                >
                  <Grid3X3 className="h-4 w-4" />
                  <span>{t('view.grid')}</span>
                </button>
              </div>
              
              {/* Configuración de columnas */}
              <div className="relative">
                <button
                  onClick={() => setShowColumnSettings(!showColumnSettings)}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Settings className="h-4 w-4" />
                </button>
                
                {showColumnSettings && (
                  <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                    <div className="p-3 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-800">{t('table.configure_columns')}</h3>
                      <p className="text-xs text-gray-500 mt-1">{t('table.configure_columns_help')}</p>
                    </div>
                    <div className="p-2">
                      {columnOrder.map((columnId, index) => {
                        const column = table.getAllLeafColumns().find(col => col.id === columnId);
                        if (!column) return null;
                        
                        return (
                          <div
                            key={columnId}
                            draggable
                            onDragStart={() => handleColumnDragStart(columnId)}
                            onDragOver={handleColumnDragOver}
                            onDrop={(e) => {
                              e.preventDefault();
                              handleColumnDrop(columnId);
                            }}
                            onDragEnd={handleColumnDragEnd}
                            className={`flex items-center space-x-2 py-2 px-2 rounded cursor-move hover:bg-gray-50 transition-colors ${
                              draggedColumn === columnId ? 'bg-blue-50 border-2 border-blue-200' : ''
                            }`}
                          >
                            {/* Drag handle */}
                            <GripVertical className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                            
                            {/* Checkbox */}
                            <input
                              type="checkbox"
                              checked={column.getIsVisible()}
                              onChange={column.getToggleVisibilityHandler()}
                              className="rounded border-gray-300"
                            />
                            
                            {/* Column name — preferimos displayName del columnConfig (admin).
                                Para tags, el columnId es "tag_<slug>" y el config keyea por tagKey,
                                así que resolvemos vía tagColumnsMeta. */}
                            <span className="text-sm text-gray-700 flex-1">
                              {(() => {
                                const tagMeta = tagColumnsMeta.find(t => t.field === columnId);
                                if (tagMeta) {
                                  return columnConfigSnapshot?.tags?.[tagMeta.tagKey]?.displayName
                                    || tagMeta.tagDisplay
                                    || tagMeta.tagKey;
                                }
                                return columnConfigSnapshot?.core?.[columnId]?.displayName
                                  || columnConfigSnapshot?.native?.[columnId]?.displayName
                                  || getColumnDisplayName(columnId);
                              })()}
                            </span>
                            
                            {/* Order indicator */}
                            <span className="text-xs text-gray-400 font-mono">
                              {(index + 1).toString().padStart(2, '0')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Botón de crear alerta - solo cuando hay productos seleccionados */}
              {!comparisonMode && selectedProducts.size > 0 && (
                <button
                  onClick={() => setShowBulkAlertModal(true)}
                  className="flex items-center gap-2 px-3 py-2 text-pink-600 hover:text-pink-700 border border-pink-300 rounded-lg hover:bg-pink-50 transition-colors"
                  title={t('alerts.create_bulk_alert')}
                >
                  <Bell className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {t('alerts.create_alert')} ({selectedProducts.size})
                  </span>
                </button>
              )}

              {/* Botón de exportar */}
              <button
                onClick={() => setExportModalOpen(true)}
                className="flex items-center gap-1 px-3 py-2 text-green-600 hover:text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
                title={t('export.export_to_excel', 'Exportar a Excel')}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12.5,10A0.5,0.5 0 0,1 13,10.5V13H15.5V14.5H13V17A0.5,0.5 0 0,1 12.5,17.5A0.5,0.5 0 0,1 12,17V14.5H9.5V13H12V10.5A0.5,0.5 0 0,1 12.5,10M8.5,12A0.5,0.5 0 0,1 9,12.5V17A0.5,0.5 0 0,1 8.5,17.5A0.5,0.5 0 0,1 8,17V12.5A0.5,0.5 0 0,1 8.5,12M15.5,12A0.5,0.5 0 0,1 16,12.5V17A0.5,0.5 0 0,1 15.5,17.5A0.5,0.5 0 0,1 15,17V12.5A0.5,0.5 0 0,1 15.5,12Z"/>
                </svg>
                <span className="text-sm font-medium">XLS</span>
              </button>

              {/* Botón limpiar todo - solo en modo comparación */}
              {comparisonMode && onClearAll && (
                <button
                  onClick={onClearAll}
                  className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <X className="h-4 w-4" />
                  <span className="text-sm">{t('compare.clear_all')}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>}

      {/* Navegación arriba de la tabla — best practice UX para tablas largas:
          el usuario no necesita scrollear al fondo para cambiar de página o ver
          contadores. Mismo componente reusado en bottom para consistencia. */}
      {(!loading || hadDataRef.current) && data.length > 0 && (
        <TableNavigation
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          totalRows={totalRows}
          totalPages={totalPages}
          onPageChange={(newIndex) =>
            setPagination({ ...pagination, pageIndex: newIndex })
          }
          onPageSizeChange={(newSize) =>
            setPagination({ ...pagination, pageSize: newSize, pageIndex: 0 })
          }
          position="top"
        />
      )}

      {/* Tabla */}
      {loading && !hadDataRef.current ? (
        /* Skeleton de tabla para primera carga */
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="flex gap-4 px-4 py-3">
              {[90, 70, 110, 80, 130, 60, 100].map((w, i) => (
                <div key={i} className="h-3 bg-gray-200 rounded animate-pulse" style={{ width: `${w}px` }} />
              ))}
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {Array.from({ length: 8 }).map((_, rowIdx) => (
              <div key={rowIdx} className="flex gap-4 px-4 py-4">
                {[80, 60, 120, 70, 100, 90, 110].map((w, colIdx) => (
                  <div key={colIdx} className="h-3 bg-gray-100 rounded animate-pulse" style={{ width: `${w}px`, animationDelay: `${(rowIdx * 7 + colIdx) * 50}ms` }} />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : !loading && data.length === 0 ? (
        /* Sin datos - mensaje limpio sin tabla */
        <div className="border border-gray-200 rounded-lg py-16 flex flex-col items-center justify-center text-gray-400">
          <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 11.625l2.25-2.25M12 11.625l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
          </svg>
          <p className="text-sm font-medium">{t('table.no_data')}</p>
        </div>
      ) : (
      <>
      {/* BulkActionBar (Publicaciones extended mode) — sticky justo arriba del
          header de la tabla. Visible sólo si extendedMode=true && hay items
          seleccionados. NO se muestra en modo comparación. */}
      {!comparisonMode && (
        <BulkActionBar
          extendedMode={extendedMode}
          selectedIds={selectedIds}
          clientId={clientId}
          onClearSelection={clearSelection}
          onAfterAction={handleAfterBulkAction}
          taxonomy={bulkTaxonomy}
        />
      )}
      <div className={`overflow-x-auto border border-gray-200 rounded-lg ${loading ? 'animate-pulse' : ''}`}>
        <table className="min-w-full table-auto divide-y divide-gray-200">
          <thead className="bg-slate-200 backdrop-blur-sm border-b-2 border-slate-400 shadow-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const colId = header.column.id.toLowerCase();
                  const centerCols = ['precio', 'precio_desc', 'precio_venta', 'precio_cuota', 'original_price', 'discounted_price', 'sale_price', 'installements_value', 'marca', 'cuotas', 'tag_marca', 'tag_pu', 'tag_pum', 'tag_categoria'];
                  const isCenter = centerCols.includes(colId);
                  return (
                  <th
                    key={header.id}
                    className={`px-4 py-2.5 ${isCenter ? 'text-center' : 'text-left'} text-xs font-bold uppercase tracking-wider text-slate-700 whitespace-nowrap sticky top-0 z-10 bg-slate-200 hover:bg-slate-300 transition-colors`}
                  >
                    {header.isPlaceholder ? null : (
                      <div className={`flex items-center space-x-2 ${isCenter ? 'justify-center' : ''}`}>
                        <div
                          className={
                            header.column.getCanSort()
                              ? 'cursor-pointer select-none flex items-center space-x-2'
                              : ''
                          }
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <div className="flex flex-col ml-2">
                              <ChevronUp
                                className={`h-3 w-3 ${
                                  header.column.getIsSorted() === 'asc'
                                    ? 'text-blue-600'
                                    : 'text-gray-400'
                                }`}
                              />
                              <ChevronDown
                                className={`h-3 w-3 ${
                                  header.column.getIsSorted() === 'desc'
                                    ? 'text-blue-600'
                                    : 'text-gray-400'
                                }`}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) => {
                // Verificar si este producto está en comparación
                const product = row.original;
                const isInComparison = isProductInComparison(product);
                const productId = product.id_catalogo || product.ID_CATALOGO || product.id;
                const isExpanded = expandedRows.has(productId);
                const isSelected = !comparisonMode && selectedProducts.has(productId);
                const variants = buyboxVariants[productId];
                const isLoading = loadingVariants.has(productId);
                // Foco lógico de navegación con teclado (BulkActionBar visible).
                const isKeyboardFocused = bulkBarVisible && focusedRowIndex === row.index;

                // Debug log for first buybox product
                if (productId === '666324') {
                }

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      ref={(el) => {
                        // Registrar/limpiar ref para scrollIntoView en nav teclado.
                        if (el) rowRefsMap.current.set(row.index, el);
                        else rowRefsMap.current.delete(row.index);
                      }}
                      className={`${
                        // Destacar en amarillo si está expandido y tiene buybox winner
                        isExpanded && product.buybox_id && (product.buybox_winner === 1 || product.buybox_winner === '1' || product.buybox_winner === true)
                          ? 'bg-yellow-100 hover:bg-yellow-200 border-l-4 border-yellow-400'
                          : // En modo comparación, no destacar productos con fondo lila
                            !comparisonMode && isInComparison
                            ? 'bg-purple-50 hover:bg-purple-100 border-l-4 border-purple-300'
                            : isKeyboardFocused
                              ? 'bg-blue-50/60 hover:bg-blue-100 border-l-4 border-blue-500 ring-1 ring-blue-300'
                              : isSelected
                                ? 'bg-blue-50 hover:bg-blue-100 border-l-4 border-blue-400'
                                : 'hover:bg-gray-50'
                      } transition-colors`}
                      style={!comparisonMode && isInComparison ? { borderLeftColor: '#c084fc' } : {}}
                    >
                      {row.getVisibleCells().map((cell) => {
                          const cellColId = cell.column.id.toLowerCase();
                          const cellCenterCols = ['precio', 'precio_desc', 'precio_venta', 'precio_cuota', 'original_price', 'discounted_price', 'sale_price', 'installements_value', 'marca', 'cuotas', 'tag_marca', 'tag_pu', 'tag_pum', 'tag_categoria'];
                          const isCellCenter = cellCenterCols.includes(cellColId);
                          return (
                          <td
                            key={cell.id}
                            className={`px-4 py-3 text-sm align-top ${
                              isCellCenter ? 'text-center' : ''
                            } ${
                              // En modo comparación, usar texto normal
                              !comparisonMode && isInComparison ? 'text-purple-900' : 'text-gray-900'
                            } ${
                              cellColId === 'titulo' ? 'w-[380px] min-w-[380px] max-w-[380px]' : 'whitespace-nowrap'
                            }`}
                          >
                            {cell.column.id.toLowerCase() === 'titulo' ? (
                              // Título completo, sin truncar. La celda crece tanto
                              // como sea necesario para mostrar todo el texto.
                              // Top-align del row para que las otras columnas anclen arriba
                              // (más legible al escanear — patrón Stripe/Linear/Notion).
                              <div
                                className="break-words"
                                style={{
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  lineHeight: '1.4'
                                }}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>
                            ) : (
                              flexRender(cell.column.columnDef.cell, cell.getContext())
                            )}
                          </td>
                          );
                        })}
                    </tr>
                    
                    {/* Renderizar fila expandida con variantes de buybox */}
                    {isExpanded && (
                      <BuyBoxVariants
                        key={`buybox-variants-${productId}`}
                        variants={variants || []}
                        isLoading={isLoading && !variants}
                        precioReferencia={product.precio_referencia || parseFloat(precioReferencia)}
                        columns={table.getAllColumns().map(col => ({ id: col.id, ...col.columnDef }))}
                        parentRow={row}
                        parentProductId={productId}
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* Paginación bottom — mismo componente reusado para consistencia visual y de UX */}
      {(!loading || hadDataRef.current) && data.length > 0 && (
        <TableNavigation
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          totalRows={totalRows}
          totalPages={totalPages}
          onPageChange={(newIndex) =>
            setPagination({ ...pagination, pageIndex: newIndex })
          }
          onPageSizeChange={(newSize) =>
            setPagination({ ...pagination, pageSize: newSize, pageIndex: 0 })
          }
          position="bottom"
        />
      )}

      {/* Modal de imagen */}
      <ImageModal
        isOpen={imageModalOpen}
        onClose={closeImageModal}
        imageUrl={selectedImage?.url || ''}
        productTitle={selectedImage?.title || ''}
      />
      
      {/* Modal de exportación */}
      <ExportModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExport}
        defaultFilename={generateDefaultFilename(tableName, totalRows, currentTableInfo?.date)}
      />
      
      {/* Progress Toast para exportación */}
      <ProgressToast
        isOpen={showExportProgress}
        onClose={() => setShowExportProgress(false)}
        progress={exportProgress}
        message={exportMessage}
        isComplete={exportComplete}
        totalRows={totalRows}
      />

      {/* Modal para crear alerta masiva */}
      <BulkAlertModal
        isOpen={showBulkAlertModal}
        onClose={() => {
          setShowBulkAlertModal(false);
          setSelectedProducts(new Set());
        }}
        selectedProducts={getSelectedProductsForAlert()}
      />

      {/* Data Issues modal — Phase 1 (Reportar / Editar) */}
      <DataIssuesModal
        isOpen={dataIssuesModalRow !== null}
        onClose={() => setDataIssuesModalRow(null)}
        row={dataIssuesModalRow}
        clientId={clientId}
        tomaId={tableName}
      />

      {/* EXTENDED MODE — Product detail modal (superadmin debug) */}
      {extendedMode && selectedDetailId && clientId && (
        <ProductDetailModal
          idCatalogo={selectedDetailId}
          clientId={clientId}
          tableName={tableName}
          onClose={() => setSelectedDetailId(null)}
        />
      )}
    </div>
  );
};

export default DataTable; 
